import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { 
  Monitor, ArrowLeft, Camera, Video, Play, Pause, Square, Terminal,
  AlertTriangle, Shield, Eye, Loader2, RefreshCw, Settings,
  ChevronUp, ChevronDown, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Home,
  Grid, LayoutGrid, Maximize2, Minimize2, HardDrive, Activity, Bot,
  Car, Users, Scan, Clock, Circle, CheckCircle, XCircle,
  Search, Filter, Download, Trash2, MoreVertical, Plus, X, Wifi, ExternalLink, Copy, Mail, Send,
} from "lucide-react";
import HermesChat from "@/components/HermesChat";
import ReactPlayer from "react-player";


import TopNav from "@/components/TopNav";
import { Card } from "@/components/ui/card";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Camera Configuration Options
const PROTOCOL_OPTIONS = [
  { value: "rtsp", label: "RTSP (Real Time Streaming Protocol)" },
  { value: "onvif", label: "ONVIF (Open Network Video Interface)" },
  { value: "http", label: "HTTP/HTTPS Stream" },
  { value: "rtmp", label: "RTMP (Real Time Messaging Protocol)" },
  { value: "hls", label: "HLS (HTTP Live Streaming)" },
  { value: "webrtc", label: "WebRTC" },
];

const RESOLUTION_OPTIONS = [
  { value: "4K", label: "4K (3840x2160)" },
  { value: "2K", label: "2K (2560x1440)" },
  { value: "1080p", label: "1080p (1920x1080)" },
  { value: "720p", label: "720p (1280x720)" },
  { value: "480p", label: "480p (854x480)" },
  { value: "360p", label: "360p (640x360)" },
];

const RECORDING_MODE_OPTIONS = [
  { value: "continuous", label: "Continuous Recording" },
  { value: "motion_triggered", label: "Motion Triggered" },
  { value: "scheduled", label: "Scheduled Recording" },
  { value: "manual", label: "Manual Only" },
];

const CAMERA_TYPE_OPTIONS = [
  { value: "fixed", label: "Fixed Camera" },
  { value: "ptz", label: "PTZ Camera" },
  { value: "drone", label: "Drone Camera" },
  { value: "thermal", label: "Thermal Camera" },
  { value: "dome", label: "Dome Camera" },
  { value: "bullet", label: "Bullet Camera" },
];

// Default new camera form
const DEFAULT_CAMERA_FORM = {
  name: "",
  location: "",
  ip_address: "",
  port: "554",
  protocol: "rtsp",
  resolution: "1080p",
  fps: 30,
  recording_mode: "continuous",
  type: "fixed",
  ptz_capable: false,
  username: "",
  password: "",
  stream_path: "/stream1",
  stream_url: "", // Direct URL for IP camera streaming (HLS, MJPEG, etc.)
};

// PTZ Control directions
const PTZ_CONTROLS = [
  { icon: ChevronUp, direction: "up", label: "Tilt Up" },
  { icon: ChevronDown, direction: "down", label: "Tilt Down" },
  { icon: ChevronLeft, direction: "left", label: "Pan Left" },
  { icon: ChevronRight, direction: "right", label: "Pan Right" },
  { icon: ZoomIn, direction: "zoom_in", label: "Zoom In" },
  { icon: ZoomOut, direction: "zoom_out", label: "Zoom Out" },
  { icon: Home, direction: "home", label: "Home Position" },
];

/**
 * Standalone component that plays a WebSocket broadcast stream via MediaSource API.
 * Used for "Go Live" broadcasts in the Drones tab.
 *
 * The relay buffers the WebM init segment and replays it to late joiners,
 * so viewers should always receive a valid init segment before media chunks.
 */
/**
 * BroadcastViewer — plays a live broadcast via HTTP snapshot polling.
 * Only updates blob when new data arrives (avoids flicker/gaps).
 */
function BroadcastViewer({ streamName, relayApiUrl }) {
  const videoRef = useRef(null);

  useEffect(() => {
    if (!streamName || !relayApiUrl) return;
    const video = videoRef.current;
    if (!video) return;
    let ms = null;
    let sb = null;
    let pollTimer = null;
    let seenChunks = new Set();
    // Queue for chunks that arrive before SourceBuffer is ready
    let pendingQueue = [];
    let closed = false;

    const baseUrl = relayApiUrl + "/hls/" + encodeURIComponent(streamName);

    const drainQueue = () => {
      while (pendingQueue.length > 0 && sb && !sb.updating && !closed) {
        const buf = pendingQueue.shift();
        try { sb.appendBuffer(buf); } catch (_) {}
      }
    };

    const appendChunk = async (seqId) => {
      try {
        const res = await fetch(baseUrl + "/segment/" + seqId);
        if (!res.ok) return;
        const buf = await res.arrayBuffer();
        if (!buf || buf.byteLength < 50) return;
        // Queue if sb not ready, append directly if it is
        if (!sb || sb.updating) {
          pendingQueue.push(buf);
        } else {
          try { sb.appendBuffer(buf); } catch (_) { pendingQueue.push(buf); }
        }
        seenChunks.add(seqId);
      } catch (_) {}
    };

    const fetchAndAppendAll = async () => {
      try {
        const resp = await fetch(baseUrl + "/segments");
        if (!resp.ok) return;
        const data = await resp.json();
        for (const id of data.segmentIds || []) {
          if (!seenChunks.has(id)) await appendChunk(id);
        }
      } catch (_) {}
    };

    const runFallback = () => {
      // Try relay snapshot for WebSocket broadcasts, fallback to backend ingest
      const relaySnap = relayApiUrl + "/hls/" + encodeURIComponent(streamName) + "/snapshot.webm";
      const backendSnap = "https://api.allbaise.com/api/drone/ingest/" + encodeURIComponent(streamName) + "/snapshot.webm";
      const trySrc = (url) => { video.src = url; video.play().catch(() => {}); video.onended = () => { video.src = url; video.play().catch(() => {}); }; };
      fetch(relaySnap, { method: "HEAD" }).then(r => { trySrc(r.ok ? relaySnap : backendSnap); }).catch(() => trySrc(backendSnap));
    };

    const init = async () => {
      try {
        const test = await fetch(baseUrl + "/segments");
        if (!test.ok) { runFallback(); return; }
      } catch { runFallback(); return; }

      ms = new MediaSource();
      // Set up handler BEFORE assigning src (prevents race condition)
      ms.onsourceopen = async () => {
        if (closed) return;
        try { sb = ms.addSourceBuffer("video/webm;codecs=vp8,opus"); }
        catch (_) { try { sb = ms.addSourceBuffer("video/webm"); } catch (_2) { runFallback(); return; } }
        sb.mode = "sequence";
        sb.onupdateend = () => {
          drainQueue();
          if (!closed && video && video.paused) video.play().catch(() => {});
        };
        // Drain anything queued before SourceBuffer was ready
        drainQueue();
        // Fetch all segments (will skip already-seen chunks)
        await fetchAndAppendAll();
        if (video && video.paused) video.play().catch(() => {});
      };
      // Set src AFTER onsourceopen handler is assigned
      video.src = URL.createObjectURL(ms);

      pollTimer = setInterval(async () => {
        if (closed) return;
        await fetchAndAppendAll();
      }, 2000);
    };

    init();

    return () => {
      closed = true;
      pendingQueue = [];
      if (pollTimer) clearInterval(pollTimer);
      if (ms && ms.readyState === "open") try { ms.endOfStream(); } catch (_) {}
      if (video) { video.onended = null; video.removeAttribute("src"); }
    };
  }, [streamName, relayApiUrl]);

  return (<video ref={videoRef} controls muted autoPlay playsInline
    className="w-full h-full object-cover" style={{background:"#000"}} />);
}

/** Fullscreen overlay for prolonged drone monitoring — keeps screen awake + native fullscreen. */
function FullscreenBroadcastViewer({ streamName, relayApiUrl, onClose }) {
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const currentRef = useRef(null);
  const wakeLockRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const [recording, setRecording] = useState(false);
  const recorderRef = useRef(null);

  const captureImage = async () => {
    const v = videoRef.current;
    if (!v) return;
    try {
      const canvas = document.createElement("canvas");
      canvas.width = v.videoWidth || 1280;
      canvas.height = v.videoHeight || 720;
      canvas.getContext("2d").drawImage(v, 0, 0, canvas.width, canvas.height);
      const dataUrl = canvas.toDataURL("image/png");
      await axios.post(`${API}/captured-media`, { type: "image", stream_name: streamName, data_url: dataUrl, mime_type: "image/png" }, { headers: getHeaders(), withCredentials: true });
      toast.success("Screenshot saved!");
    } catch { toast.error("Capture failed"); }
  };

  const toggleRecording = async () => {
    if (recording && recorderRef.current) { recorderRef.current.stop(); setRecording(false); return; }
    if (!videoRef.current?.srcObject) { toast.error("Recording only for camera/screen streams"); return; }
    try {
      const stream = videoRef.current.srcObject;
      const m = MediaRecorder.isTypeSupported("video/webm;codecs=vp8,opus") ? "video/webm;codecs=vp8,opus" : "video/webm";
      const r = new MediaRecorder(stream, { mimeType: m, videoBitsPerSecond: 2_500_000 });
      const chunks = [];
      r.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunks.push(e.data); };
      r.onstop = async () => {
        const blob = new Blob(chunks, { type: "video/webm" });
        const reader = new FileReader();
        reader.onloadend = async () => { try { await axios.post(`${API}/captured-media`, { type: "video", stream_name: streamName, data_url: reader.result, mime_type: "video/webm" }, { headers: getHeaders(), withCredentials: true }); toast.success("Video saved!"); } catch { toast.error("Save failed"); } };
        reader.readAsDataURL(blob);
      };
      r.start(1000); recorderRef.current = r; setRecording(true);
    } catch { toast.error("Recording failed"); }
  };

  useEffect(() => {
    if (!streamName || !relayApiUrl) return;
    const baseUrl = relayApiUrl + "/hls/" + encodeURIComponent(streamName);
    if (navigator.wakeLock) { navigator.wakeLock.request("screen").then(wl => { wakeLockRef.current = wl; }).catch(() => {}); }
    const el = containerRef.current;
    if (el && el.requestFullscreen) el.requestFullscreen().catch(() => {});

    const v = videoRef.current;
    if (!v) return;
    let ms = null;
    let sb = null;
    let seen = new Set();
    let closed = false;
    let pollTimer = null;

    let pendingQFS = null;
    const drainQFS = () => { while (pendingQFS && pendingQFS.length > 0 && sb && !sb.updating && !closed) { try { sb.appendBuffer(pendingQFS.shift()); } catch (_) {} } };

    const fetchSegmentsFS = async () => {
      try {
        const resp = await fetch(baseUrl + "/segments");
        if (!resp.ok) return;
        const data = await resp.json();
        for (const id of data.segmentIds || []) {
          if (seen.has(id)) continue;
          seen.add(id);
          try {
            const r2 = await fetch(baseUrl + "/segment/" + id);
            if (!r2.ok) continue;
            const buf = await r2.arrayBuffer();
            if (!buf || buf.byteLength < 50) continue;
            if (!sb || sb.updating) { if (!pendingQFS) pendingQFS = []; pendingQFS.push(buf); }
            else { try { sb.appendBuffer(buf); } catch (_) { if (!pendingQFS) pendingQFS = []; pendingQFS.push(buf); } }
          } catch (_) {}
        }
      } catch (_) {}
    };

    const runFallbackFS = () => {
      const rSnap = relayApiUrl + "/hls/" + encodeURIComponent(streamName) + "/snapshot.webm";
      const bSnap = "https://api.allbaise.com/api/drone/ingest/" + encodeURIComponent(streamName) + "/snapshot.webm";
      const setSrc = (u) => { v.src = u; v.play().catch(() => {}); v.onended = () => { v.src = u; v.play().catch(() => {}); }; };
      fetch(rSnap, { method: "HEAD" }).then(r => { setSrc(r.ok ? rSnap : bSnap); }).catch(() => setSrc(bSnap));
    };

    const initStream = async () => {
      try {
        const test = await fetch(baseUrl + "/segments");
        if (!test.ok) { runFallbackFS(); return; }
      } catch { runFallbackFS(); return; }

      ms = new MediaSource();
      ms.onsourceopen = async () => {
        if (closed) return;
        try { sb = ms.addSourceBuffer("video/webm;codecs=vp8,opus"); }
        catch (_) { try { sb = ms.addSourceBuffer("video/webm"); } catch (_2) { runFallbackFS(); return; } }
        sb.mode = "sequence";
        sb.onupdateend = () => { drainQFS(); if (!closed && v && v.paused) v.play().catch(() => {}); };
        drainQFS();
        await fetchSegmentsFS();
        if (v && v.paused) v.play().catch(() => {});
      };
      v.src = URL.createObjectURL(ms);
      pollTimer = setInterval(fetchSegmentsFS, 2000);
    };
    initStream();
    const fullscreenEnteredAt = Date.now();
    const onFsChange = () => { if (!document.fullscreenElement && (Date.now() - fullscreenEnteredAt) > 1000 && onCloseRef.current) onCloseRef.current(); };
    document.addEventListener("fullscreenchange", onFsChange);
    return () => {
      closed = true;
      if (pollTimer) clearInterval(pollTimer);
      if (ms && ms.readyState === "open") try { ms.endOfStream(); } catch (_) {}
      if (videoRef.current) videoRef.current.removeAttribute("src");
      if (wakeLockRef.current) wakeLockRef.current.release().catch(() => {});
      document.removeEventListener("fullscreenchange", onFsChange);
      if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    };
  }, [streamName, relayApiUrl]);

  return (
    <div ref={containerRef} className="fixed inset-0 z-50 bg-black flex items-center justify-center">
      <video ref={videoRef} controls muted autoPlay playsInline className="w-full h-full object-contain" style={{background:"#000"}} />
      <div className="absolute top-4 right-4 z-50 flex items-center gap-2">
        <button onClick={captureImage} className="bg-blue-600/80 hover:bg-blue-500 text-white px-2.5 py-1.5 rounded text-xs flex items-center gap-1 transition-colors" title="Screenshot"><Camera className="w-3.5 h-3.5" /> Snapshot</button>
        <button onClick={toggleRecording} className={`${recording ? "bg-red-600 animate-pulse" : "bg-red-700/80 hover:bg-red-600"} text-white px-2.5 py-1.5 rounded text-xs flex items-center gap-1 transition-colors`}><Video className="w-3.5 h-3.5" /> {recording ? "● REC" : "Record"}</button>
        <button onClick={onClose} className="bg-black/60 hover:bg-black/80 text-white px-2.5 py-1.5 rounded text-sm flex items-center gap-1.5 transition-colors"><Minimize2 className="w-4 h-4" /> Exit</button>
      </div>
      <div className="absolute bottom-4 left-4 z-50 bg-black/60 px-2 py-1 rounded text-xs text-green-400 font-medium flex items-center gap-1.5">
        <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" /> {streamName}
      </div>
    </div>
  );
}

// ── Capture/Recording Utility ──
function useCapture() {
  const captureFrame = async (videoElement, streamName, type = "image") => {
    if (!videoElement) return;
    try {
      const canvas = document.createElement("canvas");
      canvas.width = videoElement.videoWidth || 1280;
      canvas.height = videoElement.videoHeight || 720;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
      const dataUrl = canvas.toDataURL("image/png");
      await axios.post(`${API}/captured-media`, {
        type: "image", stream_name: streamName, data_url: dataUrl, mime_type: "image/png"
      }, { headers: getHeaders(), withCredentials: true });
      toast.success("Screenshot saved!");
    } catch (err) {
      toast.error("Failed to capture image");
    }
  };

  const startVideoRecording = (videoElement, streamName, onComplete) => {
    if (!videoElement || !videoElement.srcObject) {
      toast.error("No video stream available for recording");
      return null;
    }
    try {
      const stream = videoElement.srcObject;
      const m = MediaRecorder.isTypeSupported("video/webm;codecs=vp8,opus") ? "video/webm;codecs=vp8,opus" : "video/webm";
      const recorder = new MediaRecorder(stream, { mimeType: m });
      let chunks = [];
      const startTime = Date.now();

      recorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunks.push(e.data); };
      recorder.onstop = async () => {
        const blob = new Blob(chunks, { type: "video/webm" });
        const reader = new FileReader();
        reader.onloadend = async () => {
          try {
            await axios.post(`${API}/captured-media`, {
              type: "video", stream_name: streamName, data_url: reader.result, mime_type: "video/webm"
            }, { headers: getHeaders(), withCredentials: true });
            toast.success("Video clip saved!");
          } catch { toast.error("Failed to save video"); }
        };
        reader.readAsDataURL(blob);
        chunks = [];
        if (onComplete) onComplete();
      };
      recorder.start(1000);
      return recorder;
    } catch { toast.error("Recording not supported"); return null; }
  };

  return { captureFrame, startVideoRecording };
}

// ── Share Stream Dialog (by username → internal mail) ──
function ShareStreamDialog({ streamName, shareableUrl, streamType, title, onClose }) {
  const [username, setUsername] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const share = async () => {
    const u = username.trim().toLowerCase();
    if (!u) { setError('Enter a username'); return; }
    setSending(true); setError('');
    try {
      await axios.post(`${API}/streams/share`, {
        username: u, stream_name: streamName, stream_type: streamType,
        shareable_url: shareableUrl, title: title,
      }, { headers: getHeaders(), withCredentials: true });
      toast.success('Stream shared with @' + u + ' via internal mail!');
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to share';
      setError(detail);
    }
    setSending(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div className="bg-app-card border border-violet-600/40 rounded-xl p-5 max-w-sm w-full mx-4 shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-violet-400 flex items-center gap-2">
            <Mail className="w-4 h-4" /> Share via Internal Mail
          </h3>
          <button onClick={onClose} className="text-app-secondary hover:text-white p-0.5"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-xs text-app-secondary mb-3">Share "<span className="text-violet-300">{title}</span>" with any user. They will receive an internal email with the stream link.</p>
        <div className="flex items-center gap-2 bg-surface-item rounded-lg px-3 py-2 mb-1">
          <span className="text-sm text-app-secondary font-mono">@</span>
          <input value={username} onChange={e => { setUsername(e.target.value); setError(''); }}
            placeholder="username"
            className="flex-1 bg-transparent text-sm text-app focus:outline-none placeholder:text-placeholder"
            autoFocus
            onKeyDown={e => e.key === 'Enter' && share()}
          />
        </div>
        {error && <p className="text-xs text-red-400 mb-2">{error}</p>}
        <button onClick={share} disabled={sending || !username.trim()}
          className="w-full mt-2 bg-violet-600 hover:bg-violet-500 disabled:bg-slate-600 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {sending ? 'Sending...' : 'Send via Internal Mail'}
        </button>
        <p className="text-[10px] text-app-secondary text-center mt-2">The recipient will find this in their Comms Centre → Internal Mail inbox</p>
      </div>
    </div>
  );
}

export default function MonitoringCentre() {
  // State
  const [cameras, setCameras] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [recordings, setRecordings] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [systemStats, setSystemStats] = useState(null);
  const [analyticsSummary, setAnalyticsSummary] = useState(null);
  const [motionEvents, setMotionEvents] = useState([]);
  
  // Add Camera State
  const [showAddCamera, setShowAddCamera] = useState(false);
  const [newCamera, setNewCamera] = useState(DEFAULT_CAMERA_FORM);
  const [isAddingCamera, setIsAddingCamera] = useState(false);
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  
  // UI State
  const [gridLayout, setGridLayout] = useState("2x2"); // 2x2, 3x3, 4x4
  const [droneStreams, setDroneStreams] = useState([]);
  const [droneLoading, setDroneLoading] = useState(false);
  const RELAY_API = (process.env.REACT_APP_RELAY_URL || "https://stream-relay-production.up.railway.app");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [activeTab, setActiveTab] = useState("live");
  const navigate = useNavigate();

  // Broadcast (Go Live) State
  const [showBroadcastPanel, setShowBroadcastPanel] = useState(false);
  const [broadcastActive, setBroadcastActive] = useState(false);
  const [broadcastName, setBroadcastName] = useState("");
  const [broadcastViewerCount, setBroadcastViewerCount] = useState(0);
  const [broadcastStartTime, setBroadcastStartTime] = useState(null);
  const [broadcastError, setBroadcastError] = useState(null);
  const [fullscreenStream, setFullscreenStream] = useState(null); // {name, relayApiUrl}
  const [showCreateSession, setShowCreateSession] = useState(false);
  const [droneModel, setDroneModel] = useState("M350");
  const [newStreamName, setNewStreamName] = useState("");
  const [creatingDroneSession, setCreatingDroneSession] = useState(false);
  const [createdSession, setCreatedSession] = useState(null);
  const [droneSessions, setDroneSessions] = useState([]);
  const [ingestingCameras, setIngestingCameras] = useState({});
  const [connectingCamera, setConnectingCamera] = useState(null);
  const [camDevices, setCamDevices] = useState([]);
  const [selectedCamDevice, setSelectedCamDevice] = useState("");
  const [camBroadcastActive, setCamBroadcastActive] = useState(false);
  const [camBroadcastName, setCamBroadcastName] = useState("");
  const [camViewerCount, setCamViewerCount] = useState(0);
  const camPreviewRef = useRef(null);
  const camMediaRecorderRef = useRef(null);
  const camMediaStreamRef = useRef(null);
  const camWsRef = useRef(null);
  const broadcastWsRef = useRef(null);
  const broadcastActiveRef = useRef(false); // Mirror for closures
  const keepaliveRef = useRef(null);
  const wakeLockRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const broadcastPreviewRef = useRef(null);
  const broadcastDurationInterval = useRef(null);
  const RELAY_WS_URL = RELAY_API.replace(/^https:/, 'wss:');
  // Browser Ingest (HTTP) — one-click broadcast from browser
  const [showIngestPanel, setShowIngestPanel] = useState(false);
  const [ingestSource, setIngestSource] = useState(""); // "camera" | "screen"
  const [ingestStreamName, setIngestStreamName] = useState("");
  const [ingestActive, setIngestActive] = useState(false);
  const [ingestSessionResult, setIngestSessionResult] = useState(null);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [searchUsers, setSearchUsers] = useState([]);
  const [searchUserQuery, setSearchUserQuery] = useState('');
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [ingestDevices, setIngestDevices] = useState([]);
  const [ingestSelectedDevice, setIngestSelectedDevice] = useState("");
  const ingestVideoRef = useRef(null);
  const ingestMediaRecorderRef = useRef(null);
  const ingestMediaStreamRef = useRef(null);
  const [lprAnalytics, setLprAnalytics] = useState(null);
  const [lprHistory, setLprHistory] = useState([]);
  const [registeredPlates, setRegisteredPlates] = useState([]);
  const [isLoadingLpr, setIsLoadingLpr] = useState(false);
  const [newPlateForm, setNewPlateForm] = useState({ plate: "", access: "full", owner: "", vehicle: "" });
  const [lprAlerts, setLprAlerts] = useState([]);
  const [isSimulatingBlacklist, setIsSimulatingBlacklist] = useState(false);
  
  // PTZ Presets State
  const [ptzPresets, setPtzPresets] = useState([]);
  const [newPresetName, setNewPresetName] = useState("");
  
  const getHeaders = useCallback(() => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  // Fetch all data
  const fetchData = useCallback(async () => {
    try {
      const [camerasRes, statsRes, recordingsRes, alertsRes, analyticsRes, motionRes] = await Promise.all([
        axios.get(`${API}/monitoring/v2/cameras`, { headers: getHeaders(), withCredentials: true }),
        axios.get(`${API}/monitoring/v2/system-stats`, { headers: getHeaders(), withCredentials: true }),
        axios.get(`${API}/monitoring/v2/recordings`, { headers: getHeaders(), withCredentials: true }),
        axios.get(`${API}/monitoring/v2/alerts`, { headers: getHeaders(), withCredentials: true }),
        axios.get(`${API}/monitoring/v2/analytics/summary`, { headers: getHeaders(), withCredentials: true }),
        axios.get(`${API}/monitoring/v2/motion/events`, { headers: getHeaders(), withCredentials: true }),
      ]);
      setCameras(camerasRes.data);
      setSystemStats(statsRes.data);
      setRecordings(recordingsRes.data);
      setAlerts(alertsRes.data);
      setAnalyticsSummary(analyticsRes.data);
      setMotionEvents(motionRes.data);
      
      if (camerasRes.data.length > 0 && !selectedCamera) {
        setSelectedCamera(camerasRes.data[0]);
      }
    } catch (err) {
      console.error("Failed to fetch monitoring data:", err);
      toast.error("Failed to load monitoring data");
    }
  }, [getHeaders, selectedCamera]);

  useEffect(() => {
    fetchData();
    // Refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Fetch drone streams from relay
  const fetchDroneStreams = async () => {
    setDroneLoading(true);
    try {
      const res = await axios.get(RELAY_API + "/api/streams", { timeout: 5000 });
      setDroneStreams(res.data.streams || []);
    } catch { setDroneStreams([]); }
    setDroneLoading(false);
  };

  useEffect(() => {
    fetchDroneStreams();
    const iv = setInterval(fetchDroneStreams, 15000);
    return () => clearInterval(iv);
  }, []);

  // ── Broadcast (Go Live) Functions ──

  const stopBroadcast = useCallback(() => {
    broadcastActiveRef.current = false;
    setBroadcastActive(false);
    setShowBroadcastPanel(false);
    setBroadcastViewerCount(0);
    setBroadcastStartTime(null);
    setBroadcastError(null);

    if (keepaliveRef.current) {
      clearInterval(keepaliveRef.current);
      keepaliveRef.current = null;
    }
    if (wakeLockRef.current) {
      try { wakeLockRef.current.release(); } catch (_) {}
      wakeLockRef.current = null;
    }
    if (broadcastDurationInterval.current) {
      clearInterval(broadcastDurationInterval.current);
      broadcastDurationInterval.current = null;
    }

    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      try { mediaRecorderRef.current.stop(); } catch (_) {}
    }
    mediaRecorderRef.current = null;

    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(t => t.stop());
      mediaStreamRef.current = null;
    }

    if (broadcastWsRef.current) {
      try { broadcastWsRef.current.close(); } catch (_) {}
      broadcastWsRef.current = null;
    }
  }, []);

  /** Broadcast via WebSocket to the relay with keepalive + auto-reconnect. */
  const startBroadcast = useCallback(async () => {
    const name = broadcastName.trim() || "drone-" + Math.random().toString(36).slice(2, 8);
    setBroadcastError(null);
    broadcastActiveRef.current = false;

    if (!navigator.mediaDevices?.getDisplayMedia) {
      setBroadcastError("Your browser does not support screen sharing. Use Chrome or Edge.");
      return;
    }

    let stream;
    try {
      stream = await navigator.mediaDevices.getDisplayMedia({
        video: { displaySurface: "monitor", frameRate: 30 },
        audio: false,
      });
    } catch (err) {
      setBroadcastError(err.name === "NotAllowedError" || err.name === "AbortError"
        ? "Screen sharing was cancelled or denied."
        : "Could not start screen sharing: " + err.message);
      return;
    }

    mediaStreamRef.current = stream;

    // Prevent screen sleep during prolonged broadcast
    if (navigator.wakeLock) {
      navigator.wakeLock.request("screen").then(wl => { wakeLockRef.current = wl; }).catch(() => {});
    }

    // If user stops sharing via the browser bar button, stop broadcast
    stream.getVideoTracks()[0].addEventListener("ended", () => stopBroadcast());

    // Show preview
    if (broadcastPreviewRef.current) broadcastPreviewRef.current.srcObject = stream;

    // Determine best codec
    const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp8,opus")
      ? "video/webm;codecs=vp8,opus"
      : MediaRecorder.isTypeSupported("video/webm")
        ? "video/webm" : null;

    if (!mimeType) {
      setBroadcastError("Your browser does not support webm recording.");
      stream.getTracks().forEach(t => t.stop());
      return;
    }

    /** Connect WebSocket and start streaming. Called initially and on reconnect. */
    let reconnectTimer = null;
    let keepaliveInterval = null;
    const connect = () => {
      if (!broadcastActiveRef.current) return;

      const ws = new WebSocket(RELAY_WS_URL + "/ws/broadcast");
      broadcastWsRef.current = ws;

      ws.onopen = () => {
        ws.send(JSON.stringify({ type: "register", role: "broadcaster", name }));
      };

      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg.type === "registered" && msg.role === "broadcaster") {
            // Start MediaRecorder on first connection only
            if (!mediaRecorderRef.current) {
              try {
                const recorder = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: 2_500_000 });
                mediaRecorderRef.current = recorder;

                recorder.ondataavailable = (e) => {
                  // Always use broadcastWsRef.current — survives reconnects
                  const activeWs = broadcastWsRef.current;
                  if (e.data && e.data.size > 0 && activeWs && activeWs.readyState === WebSocket.OPEN) {
                    e.data.arrayBuffer()
                      .then((buf) => { try { activeWs.send(buf); } catch (_) {} })
                      .catch(() => {});
                  }
                };
                recorder.onstop = () => { stopBroadcast(); };
                recorder.start(1000);

                setBroadcastName(name);
                setBroadcastActive(true);
                broadcastActiveRef.current = true;
                setBroadcastStartTime(Date.now());
                setShowBroadcastPanel(true);
              } catch (err) {
                setBroadcastError("MediaRecorder error: " + err.message);
                stream.getTracks().forEach(t => t.stop());
              }
            }
          } else if (msg.type === "viewer-count") {
            setBroadcastViewerCount(msg.count);
          } else if (msg.type === "error") {
            setBroadcastError(msg.message);
            stopBroadcast();
          }
        } catch (_) {}
      };

      ws.onerror = () => {};

      ws.onclose = () => {
        if (broadcastActiveRef.current) {
          if (reconnectTimer) clearTimeout(reconnectTimer);
          reconnectTimer = setTimeout(connect, 1000);
        }
      };
    };

    // Start keepalive pings (lives independent of WS connection, always uses current WS ref)
    if (keepaliveRef.current) clearInterval(keepaliveRef.current);
    keepaliveInterval = setInterval(() => {
      const activeWs = broadcastWsRef.current;
      try { if (activeWs && activeWs.readyState === WebSocket.OPEN) activeWs.send(JSON.stringify({ type: "ping" })); } catch (_) {}
    }, 25000);
    keepaliveRef.current = keepaliveInterval;

    broadcastActiveRef.current = true;
    connect();
  }, [broadcastName, RELAY_WS_URL, stopBroadcast]);

  // ── Broadcast Duration Counter ──
  const [broadcastDuration, setBroadcastDuration] = useState(0);
  useEffect(() => {
    if (broadcastActive && broadcastStartTime) {
      const iv = setInterval(() => {
        setBroadcastDuration(Math.floor((Date.now() - broadcastStartTime) / 1000));
      }, 1000);
      broadcastDurationInterval.current = iv;
      return () => clearInterval(iv);
    } else {
      setBroadcastDuration(0);
    }
  }, [broadcastActive, broadcastStartTime]);

  /** Clean up broadcast on unmount */
  useEffect(() => {
    return () => {
      broadcastActiveRef.current = false;
      if (keepaliveRef.current) clearInterval(keepaliveRef.current);
      if (broadcastWsRef.current) broadcastWsRef.current.close();
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        try { mediaRecorderRef.current.stop(); } catch (_) {}
      }
      if (mediaStreamRef.current) mediaStreamRef.current.getTracks().forEach(t => t.stop());
    };
  }, []);

  // ── Live Drone Session Handlers ──

  const fetchDroneSessions = async () => {
    try {
      const res = await axios.get(`${API}/drone/sessions`, { headers: getHeaders(), withCredentials: true });
      setDroneSessions(res.data.sessions || []);
    } catch (_) {}
  };

  const handleCreateDroneSession = async () => {
    setCreatingDroneSession(true);
    try {
      const res = await axios.post(`${API}/drone/session`,
        { drone_model: droneModel, stream_name: newStreamName.trim() || undefined },
        { headers: getHeaders(), withCredentials: true }
      );
      setCreatedSession(res.data);
      toast.success("Drone session created!");
      fetchDroneSessions();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create session");
    }
    setCreatingDroneSession(false);
  };

  const handleDeleteDroneSession = async (id) => {
    try {
      await axios.delete(`${API}/drone/session/${id}`, { headers: getHeaders(), withCredentials: true });
      toast.success("Session ended");
      fetchDroneSessions();
    } catch (err) {
      toast.error("Failed to end session");
    }
  };

  // Fetch drone sessions when the Live Drone tab is active
  useEffect(() => {
    if (activeTab === "live-drone") {
      fetchDroneSessions();
    }
  }, [activeTab]);

  // ── Camera Broadcast Functions ──

  const enumerateCameras = async () => {
    try {
      // Request camera permission FIRST — without it, enumerateDevices()
      // returns empty on systems where camera was never previously granted
      // (CamLink laptop scenario).
      let permStream = null;
      try {
        // Minimal constraints to trigger permission prompt on any camera
        permStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      } catch (permErr) {
        if (permErr.name === "NotAllowedError" || permErr.name === "PermissionDeniedError") {
          setCamDevices([]);
          return;
        }
      }

      const devices = await navigator.mediaDevices.enumerateDevices();
      setCamDevices(devices.filter(d => d.kind === "videoinput"));

      if (permStream) permStream.getTracks().forEach(t => t.stop());
    } catch (_) {
      setCamDevices([]);
    }
  };

  // Auto-enumerate when Live Drone tab opens
  useEffect(() => {
    if (activeTab === "live-drone") enumerateCameras();
  }, [activeTab]);

  const stopCamBroadcast = () => {
    setCamBroadcastActive(false);
    if (camMediaRecorderRef.current && camMediaRecorderRef.current.state !== "inactive") {
      try { camMediaRecorderRef.current.stop(); } catch (_) {}
    }
    camMediaRecorderRef.current = null;
    if (camMediaStreamRef.current) {
      camMediaStreamRef.current.getTracks().forEach(t => t.stop());
      camMediaStreamRef.current = null;
    }
    if (camWsRef.current) {
      try { camWsRef.current.close(); } catch (_) {}
      camWsRef.current = null;
    }
    setCamViewerCount(0);
  };

  const startCamBroadcast = async () => {
    const name = "cam-" + Date.now().toString(36);
    setCamBroadcastName(name);
    setCamBroadcastActive(true);

    try {
      // Try exact device first, fallback to any camera
      let videoConstraints = selectedCamDevice
        ? { deviceId: { exact: selectedCamDevice }, width: { ideal: 1280 }, height: { ideal: 720 } }
        : { width: { ideal: 1280 }, height: { ideal: 720 } };

      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: videoConstraints, audio: false });
      } catch (firstErr) {
        // Fallback: try any available camera at lowest common resolution
        stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      }
      camMediaStreamRef.current = stream;
      if (camPreviewRef.current) camPreviewRef.current.srcObject = stream;

      // Determine best codec
      const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp8,opus")
        ? "video/webm;codecs=vp8,opus"
        : MediaRecorder.isTypeSupported("video/webm") ? "video/webm" : null;
      if (!mimeType) { stopCamBroadcast(); return; }

      const ws = new WebSocket(RELAY_WS_URL + "/ws/broadcast");
      camWsRef.current = ws;
      let recorderStarted = false;

      ws.onopen = () => ws.send(JSON.stringify({ type: "register", role: "broadcaster", name }));

      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg.type === "registered" && msg.role === "broadcaster" && !recorderStarted) {
            recorderStarted = true;
            const recorder = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: 2_500_000 });
            camMediaRecorderRef.current = recorder;
            recorder.ondataavailable = (e) => {
              if (e.data && e.data.size > 0) {
                const activeWs = camWsRef.current;
                if (activeWs && activeWs.readyState === WebSocket.OPEN) {
                  e.data.arrayBuffer().then(buf => { try { activeWs.send(buf); } catch (_) {} }).catch(() => {});
                }
              }
            };
            recorder.onstop = () => { if (camBroadcastActive) stopCamBroadcast(); };
            recorder.start(1000);
          } else if (msg.type === "viewer-count") {
            setCamViewerCount(msg.count);
          }
        } catch (_) {}
      };
      ws.onerror = () => { stopCamBroadcast(); };
      ws.onclose = () => { if (camBroadcastActive) stopCamBroadcast(); };
    } catch (err) {
      stopCamBroadcast();
    }
  };

  // ── Browser Ingest (HTTP) — one-click broadcast from browser to ingest endpoint ──
  const detectIngestDevices = async () => {
    try {
      let ps = null;
      try { ps = await navigator.mediaDevices.getUserMedia({ video: true, audio: false }); } catch (_) {}
      const devs = await navigator.mediaDevices.enumerateDevices();
      setIngestDevices(devs.filter(d => d.kind === "videoinput"));
      if (ps) ps.getTracks().forEach(t => t.stop());
    } catch (_) {}
  };
  const startIngestBroadcast = async () => {
    const name = ((() => { try { const u = JSON.parse(localStorage.getItem("user") || "{}"); return u.user_id ? "user-" + u.user_id.slice(0, 10) : "broadcast-" + Date.now().toString(36); } catch { return "broadcast-" + Date.now().toString(36); } })());
    setIngestStreamName(name); setIngestActive(true);
    try {
      let stream;
      if (ingestSource === "camera") {
        const c = ingestSelectedDevice ? { deviceId: { exact: ingestSelectedDevice }, width: { ideal: 1280 }, height: { ideal: 720 } } : { width: { ideal: 1280 }, height: { ideal: 720 } };
        try { stream = await navigator.mediaDevices.getUserMedia({ video: c, audio: false }); } catch { stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false }); }
      } else { stream = await navigator.mediaDevices.getDisplayMedia({ video: { frameRate: 30 }, audio: false }); }
      ingestMediaStreamRef.current = stream; if (ingestVideoRef.current) { ingestVideoRef.current.srcObject = stream; ingestVideoRef.current.play().catch(() => {}); }
      stream.getVideoTracks()[0].addEventListener("ended", () => stopIngestBroadcast());
      if (navigator.wakeLock) navigator.wakeLock.request("screen").catch(() => {});
      const m = MediaRecorder.isTypeSupported("video/webm;codecs=vp8,opus") ? "video/webm;codecs=vp8,opus" : "video/webm";
      const r = new MediaRecorder(stream, { mimeType: m, videoBitsPerSecond: 2_500_000 });
      ingestMediaRecorderRef.current = r;
      r.ondataavailable = (e) => { if (e.data && e.data.size > 0) e.data.arrayBuffer().then(buf => { fetch(`https://api.allbaise.com/api/drone/ingest/${encodeURIComponent(name)}`, { method: "POST", body: buf }).catch(() => {}); }).catch(() => {}); };
      r.onstop = () => stopIngestBroadcast(); r.start(1000);
      try { const res = await axios.post(`${API}/drone/session`, { drone_model: "M350", stream_name: name }, { headers: getHeaders(), withCredentials: true }); setIngestSessionResult(res.data); toast.success("Broadcast started! Link ready."); fetchDroneSessions(); } catch { toast.error("Failed to create link"); }
    } catch (err) { setIngestActive(false); toast.error("Failed: " + (err.message || "")); }
  };
  const stopIngestBroadcast = () => { setIngestActive(false); setShowIngestPanel(false); if (ingestMediaRecorderRef.current && ingestMediaRecorderRef.current.state !== "inactive") try { ingestMediaRecorderRef.current.stop(); } catch (_) {} ingestMediaRecorderRef.current = null; if (ingestMediaStreamRef.current) { ingestMediaStreamRef.current.getTracks().forEach(t => t.stop()); ingestMediaStreamRef.current = null; } };

  // Camera RTSP connect/disconnect
  const connectCameraFeed = async (cam) => {
    setConnectingCamera(cam.id);
    try {
      const rtsp = cam.stream_url || cam.rtsp_url || "";
      const res = await axios.post(`${API}/cameras/connect`,
        { name: cam.name, rtsp_url: rtsp, camera_id: cam.id },
        { headers: getHeaders(), withCredentials: true }
      );
      setIngestingCameras(prev => ({...prev, [cam.name]: true}));
      toast.success(`Camera "${cam.name}" connected!`);
      // Update feed_url to HLS from relay
      setCameras(prev => prev.map(c => c.id === cam.id ? {...c, feed_url: res.data.hls_url, status: "online"} : c));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to connect camera");
    }
    setConnectingCamera(null);
  };
  const disconnectCameraFeed = async (name) => {
    try {
      await axios.delete(`${API}/cameras/ingest/${encodeURIComponent(name)}`, { headers: getHeaders(), withCredentials: true });
      setIngestingCameras(prev => ({...prev, [name]: false}));
      toast.success("Camera disconnected");
    } catch (err) {
      toast.error("Failed to disconnect");
    }
  };

  // PTZ Control
  const handlePTZMove = async (direction) => {
    if (!selectedCamera) return;
    try {
      await axios.post(`${API}/monitoring/v2/ptz/${selectedCamera.id}/move`, 
        { direction, speed: 5 },
        { headers: { ...getHeaders(), "Content-Type": "application/json" }, withCredentials: true }
      );
      toast.success(`PTZ: ${direction}`);
    } catch (err) {
      toast.error("PTZ command failed");
    }
  };

  // Add Camera Functions
  const buildRtspUrl = (camera) => {
    const { protocol, ip_address, port, stream_path, username, password } = camera;
    if (protocol === "rtsp") {
      const auth = username && password ? `${username}:${password}@` : "";
      return `rtsp://${auth}${ip_address}:${port}${stream_path}`;
    } else if (protocol === "http" || protocol === "https") {
      return `${protocol}://${ip_address}:${port}${stream_path}`;
    } else if (protocol === "rtmp") {
      return `rtmp://${ip_address}:${port}${stream_path}`;
    }
    return `${protocol}://${ip_address}:${port}${stream_path}`;
  };

  const handleTestConnection = async () => {
    if (!newCamera.ip_address) {
      toast.error("Please enter an IP address");
      return;
    }
    setIsTestingConnection(true);
    
    // Simulate connection test
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // In production, this would actually test the connection
    toast.success(`Connection test successful for ${newCamera.ip_address}:${newCamera.port}`);
    setIsTestingConnection(false);
  };

  const handleAddCamera = async () => {
    if (!newCamera.name || !newCamera.ip_address) {
      toast.error("Camera name and IP address are required");
      return;
    }
    
    setIsAddingCamera(true);
    
    try {
      const rtspUrl = buildRtspUrl(newCamera);
      const cameraData = {
        name: newCamera.name,
        location: newCamera.location,
        ip_address: newCamera.ip_address,
        port: parseInt(newCamera.port) || 554,
        protocol: newCamera.protocol,
        resolution: newCamera.resolution,
        fps: parseInt(newCamera.fps) || 30,
        recording_mode: newCamera.recording_mode,
        type: newCamera.type,
        ptz_capable: newCamera.ptz_capable || newCamera.type === "ptz" || newCamera.type === "drone",
        rtsp_url: rtspUrl,
        stream_url: newCamera.stream_url || rtspUrl,
        stream_path: newCamera.stream_path,
        username: newCamera.username,
        status: "online",
        recording: newCamera.recording_mode === "continuous",
        motion_detection: true,
        analytics_enabled: true
      };
      
      const res = await axios.post(`${API}/monitoring/v2/cameras`, cameraData, {
        headers: { ...getHeaders(), "Content-Type": "application/json" },
        withCredentials: true
      });
      
      toast.success(`Camera "${newCamera.name}" added successfully`);
      setShowAddCamera(false);
      setNewCamera(DEFAULT_CAMERA_FORM);
      fetchData();
    } catch (err) {
      console.error("Add camera error:", err);
      toast.error(err.response?.data?.detail || "Failed to add camera");
    }
    
    setIsAddingCamera(false);
  };

  const handleDeleteCamera = async (cameraId) => {
    if (!confirm("Are you sure you want to delete this camera?")) return;
    
    try {
      await axios.delete(`${API}/monitoring/v2/cameras/${cameraId}`, {
        headers: getHeaders(),
        withCredentials: true
      });
      toast.success("Camera deleted");
      if (selectedCamera?.id === cameraId) {
        setSelectedCamera(null);
      }
      fetchData();
    } catch (err) {
      toast.error("Failed to delete camera");
    }
  };

  // Recording Control
  const handleStartRecording = async (cameraId) => {
    try {
      await axios.post(`${API}/monitoring/v2/recording/start/${cameraId}`, 
        { type: "manual" },
        { headers: { ...getHeaders(), "Content-Type": "application/json" }, withCredentials: true }
      );
      toast.success("Recording started");
      fetchData();
    } catch (err) {
      toast.error("Failed to start recording");
    }
  };

  const handleStopRecording = async (recordingId) => {
    try {
      await axios.post(`${API}/monitoring/v2/recording/stop/${recordingId}`, {}, 
        { headers: getHeaders(), withCredentials: true }
      );
      toast.success("Recording stopped");
      fetchData();
    } catch (err) {
      toast.error("Failed to stop recording");
    }
  };

  // YOLO Analysis
  const handleAnalyzeFrame = async () => {
    if (!selectedCamera) return;
    setIsAnalyzing(true);
    setAnalysisResult(null);
    
    try {
      const formData = new FormData();
      formData.append("camera_id", selectedCamera.id);
      
      const res = await axios.post(`${API}/monitoring/v2/analytics/analyze`, formData, {
        headers: { ...getHeaders() },
        withCredentials: true
      });
      setAnalysisResult(res.data);
      toast.success(`Detected ${res.data.total_objects} objects`);
    } catch (err) {
      toast.error("Analysis failed");
    }
    setIsAnalyzing(false);
  };

  // LPR Analysis
  const handleLPRAnalysis = async () => {
    try {
      const res = await axios.post(`${API}/monitoring/v2/analytics/lpr`, {}, {
        headers: getHeaders(),
        withCredentials: true
      });
      toast.success(`Plate detected: ${res.data.plate_number}`);
      setAnalysisResult(prev => ({ ...prev, lpr: res.data }));
    } catch (err) {
      toast.error("LPR analysis failed");
    }
  };
  
  // LPR Functions
  const fetchLprData = async () => {
    setIsLoadingLpr(true);
    try {
      const [analyticsRes, historyRes, alertsRes] = await Promise.all([
        axios.get(`${API}/monitoring/v2/lpr/analytics`, { headers: getHeaders(), withCredentials: true }),
        axios.get(`${API}/monitoring/v2/lpr/history?limit=50`, { headers: getHeaders(), withCredentials: true }),
        axios.get(`${API}/monitoring/v2/lpr/alerts?status=active`, { headers: getHeaders(), withCredentials: true })
      ]);
      setLprAnalytics(analyticsRes.data);
      setLprHistory(historyRes.data.detections || historyRes.data);
      setLprAlerts(alertsRes.data.alerts || []);
    } catch (err) {
      console.error("Failed to load LPR data:", err);
    }
    setIsLoadingLpr(false);
  };

  const handleRegisterPlate = async () => {
    if (!newPlateForm.plate) {
      toast.error("Please enter a plate number");
      return;
    }
    try {
      await axios.post(`${API}/monitoring/v2/lpr/register`, {
        plate_number: newPlateForm.plate,
        access_level: newPlateForm.access,
        owner_info: { name: newPlateForm.owner },
        vehicle_info: { description: newPlateForm.vehicle }
      }, {
        headers: { ...getHeaders(), "Content-Type": "application/json" },
        withCredentials: true
      });
      toast.success("Plate registered successfully");
      setNewPlateForm({ plate: "", access: "full", owner: "", vehicle: "" });
      fetchLprData();
    } catch (err) {
      toast.error("Failed to register plate");
    }
  };

  const handleBlacklistPlate = async (plateNumber) => {
    try {
      await axios.post(`${API}/monitoring/v2/lpr/blacklist`, {
        plate_number: plateNumber,
        reason: "Blacklisted via console"
      }, {
        headers: { ...getHeaders(), "Content-Type": "application/json" },
        withCredentials: true
      });
      toast.success("Plate blacklisted");
      fetchLprData();
    } catch (err) {
      toast.error("Failed to blacklist plate");
    }
  };
  
  // PTZ Presets
  const fetchPtzPresets = async (cameraId) => {
    try {
      const res = await axios.get(`${API}/monitoring/v2/ptz/${cameraId}/presets`, {
        headers: getHeaders(),
        withCredentials: true
      });
      setPtzPresets(res.data.presets || []);
    } catch (err) {
      console.error("Failed to fetch PTZ presets");
    }
  };

  const handleSavePreset = async () => {
    if (!selectedCamera || !newPresetName) return;
    try {
      await axios.post(`${API}/monitoring/v2/ptz/${selectedCamera.id}/presets`, {
        preset_id: ptzPresets.length + 1,
        name: newPresetName
      }, {
        headers: { ...getHeaders(), "Content-Type": "application/json" },
        withCredentials: true
      });
      toast.success(`Preset "${newPresetName}" saved`);
      setNewPresetName("");
      fetchPtzPresets(selectedCamera.id);
    } catch (err) {
      toast.error("Failed to save preset");
    }
  };

  const handleGotoPreset = async (presetId) => {
    if (!selectedCamera) return;
    try {
      await axios.post(`${API}/monitoring/v2/ptz/${selectedCamera.id}/presets/${presetId}/goto`, {}, {
        headers: getHeaders(),
        withCredentials: true
      });
      toast.success("Moving to preset position");
    } catch (err) {
      toast.error("Failed to go to preset");
    }
  };
  
  // Fetch LPR Alerts
  const fetchLprAlerts = async () => {
    try {
      const res = await axios.get(`${API}/monitoring/v2/lpr/alerts?status=active`, {
        headers: getHeaders(),
        withCredentials: true
      });
      setLprAlerts(res.data.alerts || []);
    } catch (err) {
      console.error("Failed to fetch LPR alerts:", err);
    }
  };

  // Simulate Blacklist Detection (for testing)
  const handleSimulateBlacklist = async () => {
    setIsSimulatingBlacklist(true);
    try {
      const res = await axios.post(`${API}/monitoring/v2/lpr/simulate-blacklist`, {
        camera_id: selectedCamera?.id || "cam_001"
      }, {
        headers: { ...getHeaders(), "Content-Type": "application/json" },
        withCredentials: true
      });
      toast.success(`Alert triggered for ${res.data.detection.plate_number}`, {
        description: "Check the Alerts tab to see the alert"
      });
      // Refresh alerts
      fetchLprAlerts();
      fetchAlerts();
    } catch (err) {
      toast.error("Failed to simulate blacklist detection");
    }
    setIsSimulatingBlacklist(false);
  };

  // Alert Management
  const handleAcknowledgeAlert = async (alertId) => {
    try {
      await axios.post(`${API}/monitoring/v2/alerts/${alertId}/acknowledge`, {}, {
        headers: getHeaders(),
        withCredentials: true
      });
      toast.success("Alert acknowledged");
      fetchData();
    } catch (err) {
      toast.error("Failed to acknowledge alert");
    }
  };

  const handleResolveAlert = async (alertId) => {
    try {
      await axios.post(`${API}/monitoring/v2/alerts/${alertId}/resolve`, 
        { notes: "Resolved via console" },
        { headers: { ...getHeaders(), "Content-Type": "application/json" }, withCredentials: true }
      );
      toast.success("Alert resolved");
      fetchData();
    } catch (err) {
      toast.error("Failed to resolve alert");
    }
  };

  
  // Grid layouts
  const getGridCols = () => {
    switch (gridLayout) {
      case "3x3": return "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3";
      case "4x4": return "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4";
      default: return "grid-cols-1 sm:grid-cols-2";
    }
  };

  const getStatusColor = (status) => {
    return status === "online" ? "bg-emerald-500" : "bg-red-500";
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      <TopNav />
      {/* Header */}
      <header className="border-b border-slate-700 bg-slate-900/95 backdrop-blur-lg sticky top-0 z-50">
        <div className="max-w-[1920px] mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="text-slate-400 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-600 flex items-center justify-center">
                <Monitor className="w-5 h-5 text-white" />
              </div>
              <div>
                <span className="font-['Barlow_Condensed'] text-xl font-bold tracking-wider">
                  MONITORING CENTRE
                </span>
                <p className="text-xs text-slate-400">Enterprise Surveillance System</p>
              </div>
            </div>
          </div>
          
          {/* System Stats */}
          {systemStats && (
            <div className="hidden lg:flex items-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${systemStats.system_health === "healthy" ? "bg-emerald-500" : "bg-amber-500"}`} />
                <span className="text-slate-300">{systemStats.online_cameras}/{systemStats.total_cameras} Cameras</span>
              </div>
              <div className="flex items-center gap-2">
                <Circle className="w-3 h-3 text-red-500 fill-red-500" />
                <span className="text-slate-300">{systemStats.recording_cameras} Recording</span>
              </div>
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-3 h-3 text-amber-500" />
                <span className="text-slate-300">{systemStats.active_alerts} Alerts</span>
              </div>
              <div className="flex items-center gap-2">
                <HardDrive className="w-3 h-3 text-blue-400" />
                <span className="text-slate-300">{systemStats.storage_used}</span>
              </div>
              {systemStats.yolo_available && (
                <span className="badge bg-emerald-500/20 text-emerald-400 text-xs px-2 py-1">YOLO Active</span>
              )}
            </div>
          )}
          
          <div className="flex items-center gap-2">
            <Button 
              size="sm" 
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={() => setShowAddCamera(true)}
              data-testid="add-camera-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Camera
            </Button>
            <Button variant="outline" size="sm" className="border-slate-600 text-slate-300 hover:bg-slate-800" onClick={fetchData}>
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Select value={gridLayout} onValueChange={setGridLayout}>
              <SelectTrigger className="w-24 bg-slate-800 border-slate-600 text-white">
                <LayoutGrid className="w-4 h-4 mr-1" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-600">
                <SelectItem value="2x2">2x2</SelectItem>
                <SelectItem value="3x3">3x3</SelectItem>
                <SelectItem value="4x4">4x4</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </header>

      {/* Add Camera Modal */}
      <Dialog open={showAddCamera} onOpenChange={setShowAddCamera}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <Camera className="w-5 h-5 text-emerald-500" />
              Add New Camera
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Configure camera connection settings. Supports RTSP, ONVIF, HTTP, and other protocols.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-emerald-400 uppercase tracking-wider">Basic Information</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Camera Name *</Label>
                  <Input
                    value={newCamera.name}
                    onChange={(e) => setNewCamera({...newCamera, name: e.target.value})}
                    placeholder="e.g., Main Entrance"
                    className="bg-slate-800 border-slate-600 text-white"
                    data-testid="camera-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Location</Label>
                  <Input
                    value={newCamera.location}
                    onChange={(e) => setNewCamera({...newCamera, location: e.target.value})}
                    placeholder="e.g., Building A - Front Gate"
                    className="bg-slate-800 border-slate-600 text-white"
                    data-testid="camera-location-input"
                  />
                </div>
              </div>
            </div>

            {/* Connection Settings */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-emerald-400 uppercase tracking-wider">Connection Settings</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">IP Address *</Label>
                  <Input
                    value={newCamera.ip_address}
                    onChange={(e) => setNewCamera({...newCamera, ip_address: e.target.value})}
                    placeholder="192.168.1.100"
                    className="bg-slate-800 border-slate-600 text-white"
                    data-testid="camera-ip-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Port</Label>
                  <Input
                    value={newCamera.port}
                    onChange={(e) => setNewCamera({...newCamera, port: e.target.value})}
                    placeholder="554"
                    className="bg-slate-800 border-slate-600 text-white"
                    data-testid="camera-port-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Protocol</Label>
                  <Select value={newCamera.protocol} onValueChange={(v) => setNewCamera({...newCamera, protocol: v})}>
                    <SelectTrigger className="bg-slate-800 border-slate-600 text-white" data-testid="camera-protocol-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-600">
                      {PROTOCOL_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Stream Path</Label>
                  <Input
                    value={newCamera.stream_path}
                    onChange={(e) => setNewCamera({...newCamera, stream_path: e.target.value})}
                    placeholder="/stream1"
                    className="bg-slate-800 border-slate-600 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">FPS</Label>
                  <Input
                    type="number"
                    value={newCamera.fps}
                    onChange={(e) => setNewCamera({...newCamera, fps: parseInt(e.target.value) || 30})}
                    placeholder="30"
                    className="bg-slate-800 border-slate-600 text-white"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Username (if required)</Label>
                  <Input
                    value={newCamera.username}
                    onChange={(e) => setNewCamera({...newCamera, username: e.target.value})}
                    placeholder="admin"
                    className="bg-slate-800 border-slate-600 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Password (if required)</Label>
                  <Input
                    type="password"
                    value={newCamera.password}
                    onChange={(e) => setNewCamera({...newCamera, password: e.target.value})}
                    placeholder="••••••••"
                    className="bg-slate-800 border-slate-600 text-white"
                  />
                </div>
              </div>
            </div>

            {/* Stream URL (for IP cameras with direct HLS/HTTP stream) */}
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-cyan-400 uppercase tracking-wider">Stream URL (Optional)</h3>
              <p className="text-[10px] text-slate-400">Paste a direct stream URL if your IP camera provides HLS, MJPEG, or RTSP stream link. Overrides manual IP configuration.</p>
              <Input
                value={newCamera.stream_url}
                onChange={(e) => setNewCamera({...newCamera, stream_url: e.target.value})}
                placeholder="e.g., https://camera-ip:8080/hls/stream.m3u8 or rtsp://user:pass@192.168.1.100:554/stream1"
                className="bg-slate-800 border-slate-600 text-white text-sm font-mono"
              />
            </div>

            {/* Camera Configuration */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-emerald-400 uppercase tracking-wider">Camera Configuration</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Camera Type</Label>
                  <Select value={newCamera.type} onValueChange={(v) => setNewCamera({...newCamera, type: v, ptz_capable: v === "ptz" || v === "drone"})}>
                    <SelectTrigger className="bg-slate-800 border-slate-600 text-white" data-testid="camera-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-600">
                      {CAMERA_TYPE_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Resolution</Label>
                  <Select value={newCamera.resolution} onValueChange={(v) => setNewCamera({...newCamera, resolution: v})}>
                    <SelectTrigger className="bg-slate-800 border-slate-600 text-white" data-testid="camera-resolution-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-600">
                      {RESOLUTION_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Recording Mode</Label>
                  <Select value={newCamera.recording_mode} onValueChange={(v) => setNewCamera({...newCamera, recording_mode: v})}>
                    <SelectTrigger className="bg-slate-800 border-slate-600 text-white" data-testid="camera-recording-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-600">
                      {RECORDING_MODE_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="flex items-center justify-between bg-slate-800/50 p-3 border border-slate-700">
                <div>
                  <Label className="text-slate-300">Enable PTZ Mode</Label>
                  <p className="text-xs text-slate-500">Allow pan, tilt, zoom controls for this camera</p>
                </div>
                <Switch
                  checked={newCamera.ptz_capable}
                  onCheckedChange={(checked) => setNewCamera({...newCamera, ptz_capable: checked})}
                  data-testid="camera-ptz-switch"
                />
              </div>
            </div>

            {/* Connection Preview */}
            <div className="bg-slate-800/50 p-4 border border-slate-700 space-y-2">
              <Label className="text-slate-400 text-xs uppercase">Generated Stream URL</Label>
              <code className="block text-emerald-400 text-sm font-mono break-all">
                {buildRtspUrl(newCamera) || "Configure IP address to generate URL"}
              </code>
            </div>
          </div>

          <DialogFooter className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={handleTestConnection}
              disabled={isTestingConnection || !newCamera.ip_address}
              className="border-slate-600 text-slate-300"
            >
              {isTestingConnection ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wifi className="w-4 h-4 mr-2" />}
              Test Connection
            </Button>
            <Button 
              variant="outline" 
              onClick={() => setShowAddCamera(false)}
              className="border-slate-600 text-slate-300"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleAddCamera}
              disabled={isAddingCamera || !newCamera.name || !newCamera.ip_address}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="save-camera-btn"
            >
              {isAddingCamera ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Add Camera
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="max-w-[1920px] mx-auto flex flex-col lg:flex-row">
        {/* Main Content */}
        <div className="flex-1 p-2 sm:p-4">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="bg-slate-800 border border-slate-700 mb-4 overflow-x-auto">
              <TabsTrigger value="live" className="data-[state=active]:bg-red-600" data-testid="tab-live">
                <Video className="w-4 h-4 mr-2" />
                Live View
              </TabsTrigger>
              <TabsTrigger value="cameras" className="data-[state=active]:bg-red-600" data-testid="tab-cameras">
                <Camera className="w-4 h-4 mr-2" />
                Cameras
              </TabsTrigger>
              <TabsTrigger value="live-cameras" className="data-[state=active]:bg-red-600" data-testid="tab-live-cameras">
                <Monitor className="w-4 h-4 mr-2" />
                Live Cameras
              </TabsTrigger>
              <TabsTrigger value="analytics" className="data-[state=active]:bg-red-600" data-testid="tab-analytics">
                <Eye className="w-4 h-4 mr-2" />
                AI Analytics
              </TabsTrigger>
              <TabsTrigger value="lpr" className="data-[state=active]:bg-red-600" data-testid="tab-lpr" onClick={fetchLprData}>
                <Car className="w-4 h-4 mr-2" />
                LPR
              </TabsTrigger>
              <TabsTrigger value="recordings" className="data-[state=active]:bg-red-600" data-testid="tab-recordings">
                <HardDrive className="w-4 h-4 mr-2" />
                Recordings
              </TabsTrigger>
              <TabsTrigger value="live-drone" className="data-[state=active]:bg-red-600" data-testid="tab-live-drone">
                <Video className="w-4 h-4 mr-2" />
                Live Drone
              </TabsTrigger>
              <TabsTrigger value="smart-drone" className="data-[state=active]:bg-sky-600" data-testid="tab-smart-drone">
                <Camera className="w-4 h-4 mr-2" />
                Smart Drone
              </TabsTrigger>
              <TabsTrigger value="drones" className="data-[state=active]:bg-red-600" data-testid="tab-drones">
                <Wifi className="w-4 h-4 mr-2" />
                Drones
              </TabsTrigger>
              <TabsTrigger value="alerts" className="data-[state=active]:bg-red-600" data-testid="tab-alerts">
                <AlertTriangle className="w-4 h-4 mr-2" />
                Alerts
              </TabsTrigger>
            </TabsList>

            {/* Live View Tab */}
            <TabsContent value="live" className="space-y-4">
              <div className="flex gap-4">
                {/* Camera Grid */}
                <div className="flex-1">
                  <div className={`grid ${getGridCols()} gap-2`}>
                    {cameras.slice(0, gridLayout === "4x4" ? 16 : gridLayout === "3x3" ? 9 : 4).map((cam) => (
                      <div
                        key={cam.id}
                        onClick={() => setSelectedCamera(cam)}
                        className={`relative aspect-video bg-slate-800 cursor-pointer border-2 transition-all ${
                          selectedCamera?.id === cam.id ? "border-red-500" : "border-transparent hover:border-slate-600"
                        }`}
                        data-testid={`camera-feed-${cam.id}`}
                      >
                        <img 
                          src={cam.feed_url} 
                          alt={cam.name}
                          className="w-full h-full object-cover"
                        />
                        {/* Overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                        
                        {/* Status Indicator */}
                        <div className="absolute top-2 left-2 flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${getStatusColor(cam.status)} ${cam.status === "online" ? "animate-pulse" : ""}`} />
                          <span className="text-xs text-white font-medium uppercase">{cam.status}</span>
                        </div>
                        
                        {/* Recording Indicator */}
                        {cam.recording && (
                          <div className="absolute top-2 right-2 flex items-center gap-1 bg-red-600 px-2 py-0.5 rounded">
                            <Circle className="w-2 h-2 fill-white" />
                            <span className="text-xs text-white">REC</span>
                          </div>
                        )}
                        
                        {/* Camera Info */}
                        <div className="absolute bottom-2 left-2 right-2">
                          <p className="text-white font-semibold text-sm truncate">{cam.name}</p>
                          <p className="text-slate-300 text-xs truncate">{cam.location}</p>
                        </div>
                        
                        {/* Resolution Badge */}
                        <div className="absolute bottom-2 right-2">
                          <span className="text-xs text-slate-400">{cam.resolution}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Control Panel */}
                {selectedCamera && (
                  <div className="w-80 space-y-4">
                    {/* Selected Camera Info */}
                    <div className="bg-slate-800 border border-slate-700 p-4">
                      <h3 className="font-semibold text-white mb-2">{selectedCamera.name}</h3>
                      <p className="text-sm text-slate-400 mb-3">{selectedCamera.location}</p>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="bg-slate-700/50 p-2">
                          <span className="text-slate-400">Resolution</span>
                          <p className="text-white font-medium">{selectedCamera.resolution}</p>
                        </div>
                        <div className="bg-slate-700/50 p-2">
                          <span className="text-slate-400">FPS</span>
                          <p className="text-white font-medium">{selectedCamera.fps}</p>
                        </div>
                        <div className="bg-slate-700/50 p-2">
                          <span className="text-slate-400">Type</span>
                          <p className="text-white font-medium capitalize">{selectedCamera.type}</p>
                        </div>
                        <div className="bg-slate-700/50 p-2">
                          <span className="text-slate-400">IP</span>
                          <p className="text-white font-medium text-xs">{selectedCamera.ip_address}</p>
                        </div>
                      </div>
                    </div>

                    {/* PTZ Controls */}
                    {selectedCamera.ptz_capable && (
                      <div className="bg-slate-800 border border-slate-700 p-4">
                        <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                          <Settings className="w-4 h-4" />
                          PTZ Control
                        </h4>
                        <div className="grid grid-cols-3 gap-2">
                          <div />
                          <Button size="sm" variant="outline" className="border-slate-600" onClick={() => handlePTZMove("up")}>
                            <ChevronUp className="w-4 h-4" />
                          </Button>
                          <div />
                          <Button size="sm" variant="outline" className="border-slate-600" onClick={() => handlePTZMove("left")}>
                            <ChevronLeft className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="outline" className="border-slate-600" onClick={() => handlePTZMove("home")}>
                            <Home className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="outline" className="border-slate-600" onClick={() => handlePTZMove("right")}>
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="outline" className="border-slate-600" onClick={() => handlePTZMove("zoom_out")}>
                            <ZoomOut className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="outline" className="border-slate-600" onClick={() => handlePTZMove("down")}>
                            <ChevronDown className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="outline" className="border-slate-600" onClick={() => handlePTZMove("zoom_in")}>
                            <ZoomIn className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Recording Controls */}
                    <div className="bg-slate-800 border border-slate-700 p-4">
                      <h4 className="text-sm font-semibold text-white mb-3">Recording</h4>
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          className={selectedCamera.recording ? "bg-red-600 hover:bg-red-700" : "bg-emerald-600 hover:bg-emerald-700"}
                          onClick={() => selectedCamera.recording ? toast.info("Stop from Recordings tab") : handleStartRecording(selectedCamera.id)}
                          data-testid="record-btn"
                        >
                          {selectedCamera.recording ? (
                            <><Square className="w-4 h-4 mr-1" /> Recording</>
                          ) : (
                            <><Circle className="w-4 h-4 mr-1 fill-current" /> Start</>
                          )}
                        </Button>
                      </div>
                    </div>

                    {/* Quick Analysis */}
                    <div className="bg-slate-800 border border-slate-700 p-4">
                      <h4 className="text-sm font-semibold text-white mb-3">AI Analysis</h4>
                      <div className="space-y-2">
                        <Button 
                          size="sm" 
                          className="w-full bg-blue-600 hover:bg-blue-700"
                          onClick={handleAnalyzeFrame}
                          disabled={isAnalyzing}
                          data-testid="analyze-btn"
                        >
                          {isAnalyzing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
                          YOLO Detection
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="w-full border-slate-600"
                          onClick={handleLPRAnalysis}
                          data-testid="lpr-btn"
                        >
                          <Scan className="w-4 h-4 mr-2" />
                          License Plate
                        </Button>
                      </div>
                      
                      {analysisResult && (
                        <div className="mt-3 p-2 bg-slate-700/50 text-xs">
                          <p className="text-emerald-400 font-semibold mb-1">Detection Results:</p>
                          <p className="text-white">Objects: {analysisResult.total_objects}</p>
                          {analysisResult.counts && Object.entries(analysisResult.counts).map(([cls, count]) => (
                            <p key={cls} className="text-slate-300">{cls}: {count}</p>
                          ))}
                          {analysisResult.lpr && (
                            <p className="text-amber-400 mt-1">Plate: {analysisResult.lpr.plate_number}</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Cameras Management Tab */}
            <TabsContent value="cameras" className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-white">Camera Management</h2>
                <Button 
                  className="bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => setShowAddCamera(true)}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add New Camera
                </Button>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                {cameras.map((cam) => (
                  <div key={cam.id} className="bg-slate-800 border border-slate-700 overflow-hidden">
                    {/* Camera Preview */}
                    <div className="relative aspect-video">
                      <img 
                        src={cam.feed_url} 
                        alt={cam.name}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/30" />
                      
                      {/* Status Badge */}
                      <div className="absolute top-2 left-2 flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${getStatusColor(cam.status)} ${cam.status === "online" ? "animate-pulse" : ""}`} />
                        <span className="text-xs text-white font-medium uppercase bg-black/50 px-2 py-0.5">{cam.status}</span>
                      </div>
                      
                      {/* Type Badge */}
                      <div className="absolute top-2 right-2">
                        <span className={`text-xs font-medium px-2 py-0.5 ${
                          cam.type === "drone" ? "bg-purple-600" :
                          cam.type === "ptz" ? "bg-blue-600" :
                          cam.type === "thermal" ? "bg-orange-600" :
                          "bg-slate-600"
                        }`}>
                          {cam.type?.toUpperCase()}
                        </span>
                      </div>
                      
                      {/* Recording Indicator */}
                      {cam.recording && (
                        <div className="absolute bottom-2 left-2 flex items-center gap-1 bg-red-600 px-2 py-0.5">
                          <Circle className="w-2 h-2 fill-white" />
                          <span className="text-xs text-white">RECORDING</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Camera Details */}
                    <div className="p-4 space-y-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="text-white font-semibold">{cam.name}</h3>
                          <p className="text-sm text-slate-400">{cam.location}</p>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                          onClick={() => handleDeleteCamera(cam.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      
                      {/* Camera Specs */}
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="bg-slate-700/50 p-2">
                          <span className="text-slate-400 block">IP Address</span>
                          <span className="text-white font-mono">{cam.ip_address}</span>
                        </div>
                        <div className="bg-slate-700/50 p-2">
                          <span className="text-slate-400 block">Port</span>
                          <span className="text-white font-mono">{cam.port || "554"}</span>
                        </div>
                        <div className="bg-slate-700/50 p-2">
                          <span className="text-slate-400 block">Resolution</span>
                          <span className="text-white">{cam.resolution}</span>
                        </div>
                        <div className="bg-slate-700/50 p-2">
                          <span className="text-slate-400 block">FPS</span>
                          <span className="text-white">{cam.fps}</span>
                        </div>
                        <div className="bg-slate-700/50 p-2">
                          <span className="text-slate-400 block">Protocol</span>
                          <span className="text-white uppercase">{cam.protocol || "RTSP"}</span>
                        </div>
                        <div className="bg-slate-700/50 p-2">
                          <span className="text-slate-400 block">Recording</span>
                          <span className="text-white capitalize">{cam.recording_mode || "Manual"}</span>
                        </div>
                      </div>
                      
                      {/* Features */}
                      <div className="flex flex-wrap gap-2">
                        {cam.ptz_capable && (
                          <span className="badge bg-blue-500/20 text-blue-400 text-xs">PTZ Enabled</span>
                        )}
                        {cam.motion_detection && (
                          <span className="badge bg-amber-500/20 text-amber-400 text-xs">Motion Detection</span>
                        )}
                        {cam.analytics_enabled && (
                          <span className="badge bg-emerald-500/20 text-emerald-400 text-xs">AI Analytics</span>
                        )}
                      </div>
                      
                      {/* RTSP URL */}
                      {cam.rtsp_url && (
                        <div className="bg-slate-900/50 p-2 border border-slate-700">
                          <span className="text-xs text-slate-400 block mb-1">Stream URL</span>
                          <code className="text-xs text-emerald-400 font-mono break-all">{cam.rtsp_url}</code>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                
                {/* Empty State */}
                {cameras.length === 0 && (
                  <div className="col-span-full bg-slate-800 border border-slate-700 p-12 text-center">
                    <Camera className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-white mb-2">No Cameras Added</h3>
                    <p className="text-slate-400 mb-4">Add your first camera to start monitoring</p>
                    <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setShowAddCamera(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Add Camera
                    </Button>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Live Cameras Tab */}
            <TabsContent value="live-cameras" className="space-y-4">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                  <Monitor className="w-5 h-5 text-red-400" /> Live Cameras
                </h2>
                <div className="flex items-center gap-2">
                  <button onClick={() => setShowAddCamera(true)}
                    className="text-xs bg-red-600 hover:bg-red-500 text-white px-3 py-1.5 rounded font-medium flex items-center gap-1">
                    <Plus className="w-3 h-3" /> Add Camera
                  </button>
                  <button onClick={fetchData}
                    className="text-xs bg-slate-700 hover:bg-slate-600 text-white px-2 py-1.5 rounded flex items-center gap-1">
                    <RefreshCw className="w-3 h-3" /> Refresh
                  </button>
                </div>
              </div>

              {cameras.length === 0 ? (
                <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-10 text-center">
                  <Monitor className="w-10 h-10 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400 text-sm mb-1">No cameras configured</p>
                  <p className="text-slate-500 text-xs">Click "Add Camera" to set up an IP camera with a stream URL</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {cameras.map((cam) => (
                    <div key={cam.id} className="bg-slate-800/80 border border-slate-700 rounded-lg overflow-hidden">
                      <div className="relative aspect-video bg-black">
                        {cam.feed_url && !cam.feed_url.includes("unsplash") ? (
                          <video controls muted autoPlay playsInline className="w-full h-full object-cover" style={{ background: "#000" }}>
                            <source src={cam.feed_url} type={cam.feed_url.endsWith(".m3u8") ? "application/x-mpegURL" : cam.feed_url.endsWith(".webm") ? "video/webm" : "video/mp4"} />
                          </video>
                        ) : (
                          <img src={cam.feed_url} alt={cam.name} className="w-full h-full object-cover" />
                        )}
                        <div className="absolute top-2 left-2 flex items-center gap-1.5 bg-black/60 px-2 py-0.5 rounded">
                          <span className={`w-2 h-2 rounded-full ${cam.status === "online" ? "bg-green-500 animate-pulse" : "bg-slate-500"}`} />
                          <span className="text-[10px] text-green-400 font-medium">{cam.status === "online" ? "LIVE" : "OFFLINE"}</span>
                        </div>
                      </div>
                      <div className="p-3">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-white truncate">{cam.name}</p>
                          {cam.resolution && <span className="text-[10px] text-slate-500">{cam.resolution}</span>}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-[10px] bg-slate-700 text-slate-400 px-1.5 py-0.5 rounded capitalize">{cam.type || "fixed"}</span>
                          {cam.location && <span className="text-[10px] text-slate-500 truncate">{cam.location}</span>}
                        </div>
                        {cam.feed_url && !cam.feed_url.includes("unsplash") && (
                          <div className="mt-2 flex items-center gap-1">
                            <span className="text-[9px] text-cyan-400/80 truncate">{cam.feed_url}</span>
                            <button onClick={() => { navigator.clipboard.writeText(cam.feed_url); toast.success("URL copied"); }}
                              className="text-[9px] text-cyan-400 hover:text-cyan-300 shrink-0">Copy</button>
                          </div>
                        )}
                        {(cam.stream_url?.startsWith("rtsp://") || cam.rtsp_url?.startsWith("rtsp://")) && (
                          <div className="mt-2">
                            {ingestingCameras[cam.name] ? (
                              <button onClick={() => disconnectCameraFeed(cam.name)}
                                className="w-full text-[10px] bg-red-700 hover:bg-red-600 text-white px-2 py-1 rounded font-medium">
                                Disconnect Feed
                              </button>
                            ) : (
                              <button onClick={() => connectCameraFeed(cam)} disabled={connectingCamera === cam.id}
                                className="w-full text-[10px] bg-emerald-700 hover:bg-emerald-600 disabled:bg-slate-600 text-white px-2 py-1 rounded font-medium flex items-center justify-center gap-1">
                                {connectingCamera === cam.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wifi className="w-3 h-3" />}
                                {connectingCamera === cam.id ? "Connecting..." : "Connect Live Feed"}
                              </button>
                            )}
                          </div>
                        )}
                        {!cam.stream_url && !cam.rtsp_url && (
                          <div className="mt-2">
                            <p className="text-[9px] text-slate-600 italic">No RTSP stream configured. Add a Stream URL to enable live feed.</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Analytics Tab */}
            <TabsContent value="analytics" className="space-y-4">
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                {/* Stats Cards */}
                {analyticsSummary && (
                  <>
                    <div className="bg-slate-800 border border-slate-700 p-4">
                      <div className="flex items-center gap-3">
                        <Users className="w-8 h-8 text-blue-400" />
                        <div>
                          <p className="text-2xl font-bold text-white">{analyticsSummary.by_type?.person || 0}</p>
                          <p className="text-xs text-slate-400">People Detected</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-slate-800 border border-slate-700 p-4">
                      <div className="flex items-center gap-3">
                        <Car className="w-8 h-8 text-emerald-400" />
                        <div>
                          <p className="text-2xl font-bold text-white">{(analyticsSummary.by_type?.car || 0) + (analyticsSummary.by_type?.truck || 0)}</p>
                          <p className="text-xs text-slate-400">Vehicles Detected</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-slate-800 border border-slate-700 p-4">
                      <div className="flex items-center gap-3">
                        <Scan className="w-8 h-8 text-amber-400" />
                        <div>
                          <p className="text-2xl font-bold text-white">{analyticsSummary.plates_detected || 0}</p>
                          <p className="text-xs text-slate-400">Plates Read</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-slate-800 border border-slate-700 p-4">
                      <div className="flex items-center gap-3">
                        <Activity className="w-8 h-8 text-red-400" />
                        <div>
                          <p className="text-2xl font-bold text-white">{analyticsSummary.motion_events || 0}</p>
                          <p className="text-xs text-slate-400">Motion Events</p>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Detection History */}
              <div className="bg-slate-800 border border-slate-700 p-4">
                <h3 className="font-semibold text-white mb-4">Recent Detections</h3>
                <div className="space-y-2">
                  {analysisResult?.detections?.length > 0 ? (
                    analysisResult.detections.map((det, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-slate-700/50 p-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 flex items-center justify-center ${
                            det.class === "person" ? "bg-blue-500/20" :
                            det.class === "car" || det.class === "truck" ? "bg-emerald-500/20" : "bg-slate-600"
                          }`}>
                            {det.class === "person" ? <Users className="w-4 h-4 text-blue-400" /> :
                             <Car className="w-4 h-4 text-emerald-400" />}
                          </div>
                          <div>
                            <p className="text-white font-medium capitalize">{det.class}</p>
                            <p className="text-xs text-slate-400">Confidence: {(det.confidence * 100).toFixed(0)}%</p>
                          </div>
                        </div>
                        <div className="text-xs text-slate-400">
                          Box: ({det.bbox.x1}, {det.bbox.y1}) - ({det.bbox.x2}, {det.bbox.y2})
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-slate-400 text-center py-8">Run YOLO detection from Live View to see results</p>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* LPR Tab */}
            <TabsContent value="lpr" className="space-y-4">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* LPR Analytics Summary */}
                <div className="lg:col-span-2 space-y-4">
                  {/* Stats Cards */}
                  {lprAnalytics && (
                    <div className="grid grid-cols-4 gap-4">
                      <div className="bg-slate-800 border border-slate-700 p-4">
                        <p className="text-2xl font-bold text-white">{lprAnalytics.total_detections || 0}</p>
                        <p className="text-xs text-slate-400">Total Detections</p>
                      </div>
                      <div className="bg-slate-800 border border-slate-700 p-4">
                        <p className="text-2xl font-bold text-emerald-400">{lprAnalytics.registered_vehicles || 0}</p>
                        <p className="text-xs text-slate-400">Registered</p>
                      </div>
                      <div className="bg-slate-800 border border-slate-700 p-4">
                        <p className="text-2xl font-bold text-amber-400">{lprAnalytics.unknown_vehicles || 0}</p>
                        <p className="text-xs text-slate-400">Unknown</p>
                      </div>
                      <div className="bg-slate-800 border border-slate-700 p-4">
                        <p className="text-2xl font-bold text-red-400">{lprAnalytics.denied_entries || 0}</p>
                        <p className="text-xs text-slate-400">Denied</p>
                      </div>
                    </div>
                  )}

                  {/* Detection History */}
                  <div className="bg-slate-800 border border-slate-700 p-4">
                    <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                      <Clock className="w-5 h-5 text-amber-400" />
                      Recent Plate Detections
                    </h3>
                    <ScrollArea className="h-[400px]">
                      {lprHistory.length > 0 ? (
                        <div className="space-y-2">
                          {lprHistory.map((detection, idx) => (
                            <div key={idx} className="flex items-center justify-between bg-slate-700/50 p-3">
                              <div className="flex items-center gap-3">
                                <div className={`px-3 py-1 font-mono text-sm font-bold ${
                                  detection.access_level === "full" ? "bg-emerald-600" :
                                  detection.access_level === "denied" ? "bg-red-600" :
                                  detection.access_level === "restricted" ? "bg-amber-600" :
                                  "bg-slate-600"
                                }`}>
                                  {detection.plate_number}
                                </div>
                                <div>
                                  <p className={`text-sm capitalize ${
                                    detection.access_level === "full" ? "text-emerald-400" :
                                    detection.access_level === "denied" ? "text-red-400" :
                                    "text-slate-300"
                                  }`}>
                                    {detection.access_level || "Unknown"}
                                  </p>
                                  <p className="text-xs text-slate-500">
                                    {new Date(detection.timestamp).toLocaleString()}
                                  </p>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <span className="text-xs text-slate-400">
                                  {(detection.confidence * 100).toFixed(0)}% conf
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-12 text-slate-500">
                          <Scan className="w-12 h-12 mx-auto mb-3 text-slate-600" />
                          <p>No plate detections yet</p>
                        </div>
                      )}
                    </ScrollArea>
                  </div>
                </div>

                {/* Register New Plate */}
                <div className="space-y-4">
                  <div className="bg-slate-800 border border-slate-700 p-4">
                    <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                      <Plus className="w-5 h-5 text-emerald-400" />
                      Register Plate
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <label className="text-xs text-slate-400 mb-1 block">Plate Number</label>
                        <Input
                          value={newPlateForm.plate}
                          onChange={(e) => setNewPlateForm(prev => ({ ...prev, plate: e.target.value.toUpperCase() }))}
                          placeholder="ABC 123 GP"
                          className="bg-slate-700 border-slate-600 text-white font-mono"
                          data-testid="lpr-plate-input"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-400 mb-1 block">Access Level</label>
                        <Select value={newPlateForm.access} onValueChange={(v) => setNewPlateForm(prev => ({ ...prev, access: v }))}>
                          <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-800 border-slate-600">
                            <SelectItem value="full">Full Access</SelectItem>
                            <SelectItem value="restricted">Restricted</SelectItem>
                            <SelectItem value="visitor">Visitor</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-xs text-slate-400 mb-1 block">Owner Name</label>
                        <Input
                          value={newPlateForm.owner}
                          onChange={(e) => setNewPlateForm(prev => ({ ...prev, owner: e.target.value }))}
                          placeholder="John Doe"
                          className="bg-slate-700 border-slate-600 text-white"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-400 mb-1 block">Vehicle Description</label>
                        <Input
                          value={newPlateForm.vehicle}
                          onChange={(e) => setNewPlateForm(prev => ({ ...prev, vehicle: e.target.value }))}
                          placeholder="White Toyota Corolla"
                          className="bg-slate-700 border-slate-600 text-white"
                        />
                      </div>
                      <Button
                        onClick={handleRegisterPlate}
                        className="w-full bg-emerald-600 hover:bg-emerald-700"
                        disabled={!newPlateForm.plate}
                        data-testid="register-plate-btn"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Register Plate
                      </Button>
                    </div>
                  </div>

                  {/* Top Plates */}
                  {lprAnalytics?.top_plates && (
                    <div className="bg-slate-800 border border-slate-700 p-4">
                      <h3 className="font-semibold text-white mb-3">Frequent Plates</h3>
                      <div className="space-y-2">
                        {lprAnalytics.top_plates.map((plate, idx) => (
                          <div key={idx} className="flex items-center justify-between bg-slate-700/50 p-2">
                            <span className="font-mono text-sm text-white">{plate.plate}</span>
                            <span className="text-xs text-slate-400">{plate.count} times</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* LPR Alerts */}
                  <div className="bg-slate-800 border border-red-700 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-white flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-red-500" />
                        Blacklist Alerts
                        {lprAlerts.length > 0 && (
                          <span className="bg-red-600 text-white text-xs px-2 py-0.5 rounded-full animate-pulse">
                            {lprAlerts.length}
                          </span>
                        )}
                      </h3>
                      <Button
                        onClick={handleSimulateBlacklist}
                        disabled={isSimulatingBlacklist}
                        size="sm"
                        className="bg-red-600 hover:bg-red-700"
                        data-testid="simulate-blacklist-btn"
                      >
                        {isSimulatingBlacklist ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <AlertTriangle className="w-4 h-4 mr-1" />
                            Test Alert
                          </>
                        )}
                      </Button>
                    </div>
                    <ScrollArea className="h-[200px]">
                      {lprAlerts.length > 0 ? (
                        <div className="space-y-2">
                          {lprAlerts.map((alert, idx) => (
                            <div key={idx} className="bg-red-900/30 border border-red-700/50 p-3">
                              <p className="text-white font-semibold text-sm">{alert.title}</p>
                              <p className="text-red-300 text-xs mt-1">{alert.description}</p>
                              <div className="flex items-center justify-between mt-2">
                                <span className="text-xs text-red-400">
                                  {new Date(alert.created_at).toLocaleString()}
                                </span>
                                <span className="text-xs bg-red-600 px-2 py-0.5 text-white">{alert.severity}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-slate-500">
                          <CheckCircle className="w-8 h-8 mx-auto mb-2 text-emerald-500" />
                          <p className="text-sm">No blacklist alerts</p>
                          <p className="text-xs text-slate-600 mt-1">Click "Test Alert" to simulate</p>
                        </div>
                      )}
                    </ScrollArea>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Recordings Tab */}
            <TabsContent value="recordings" className="space-y-4">
              <div className="bg-slate-800 border border-slate-700">
                <div className="p-4 border-b border-slate-700 flex items-center justify-between">
                  <h3 className="font-semibold text-white">Recording Archive</h3>
                  <div className="flex items-center gap-2">
                    <Input placeholder="Search recordings..." className="w-64 bg-slate-700 border-slate-600 text-white" />
                  </div>
                </div>
                <ScrollArea className="h-[500px]">
                  <table className="w-full">
                    <thead className="bg-slate-700/50 sticky top-0">
                      <tr className="text-left text-xs text-slate-400">
                        <th className="p-3">Camera</th>
                        <th className="p-3">Start Time</th>
                        <th className="p-3">Duration</th>
                        <th className="p-3">Type</th>
                        <th className="p-3">Detections</th>
                        <th className="p-3">Size</th>
                        <th className="p-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recordings.map((rec) => (
                        <tr key={rec.id} className="border-t border-slate-700 hover:bg-slate-700/30">
                          <td className="p-3 text-white">{rec.camera_name}</td>
                          <td className="p-3 text-slate-300 text-sm">{new Date(rec.start_time).toLocaleString()}</td>
                          <td className="p-3 text-slate-300">{Math.floor(rec.duration / 60)}:{(rec.duration % 60).toString().padStart(2, '0')}</td>
                          <td className="p-3">
                            <span className={`badge text-xs ${rec.type === "motion_triggered" ? "bg-amber-500/20 text-amber-400" : "bg-blue-500/20 text-blue-400"}`}>
                              {rec.type}
                            </span>
                          </td>
                          <td className="p-3">
                            <div className="flex gap-1">
                              {rec.detections?.map((d, i) => (
                                <span key={i} className="badge bg-slate-600 text-xs">{d}</span>
                              ))}
                            </div>
                          </td>
                          <td className="p-3 text-slate-300">{rec.file_size}</td>
                          <td className="p-3">
                            <div className="flex gap-1">
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-400 hover:text-white">
                                <Play className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-400 hover:text-white">
                                <Download className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </ScrollArea>
              </div>
            </TabsContent>

            {/* Live Drone Tab — DJI auto-detect + camera broadcast + shareable links */}
            <TabsContent value="live-drone" className="space-y-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <Video className="w-4 h-4 text-red-400" /> Live Drone Sessions
                </h3>
                <button onClick={() => { fetchDroneStreams(); fetchDroneSessions(); }}
                  className="text-xs bg-slate-700 hover:bg-slate-600 text-white px-2 py-1 rounded flex items-center gap-1">
                  <RefreshCw className="w-3 h-3" /> Refresh
                </button>
              </div>

              {/* One-Click Broadcast — browser captures video, sends via HTTP (no FFmpeg) */}
              <Card className="bg-slate-800/80 border border-emerald-500/40">
                <div className="p-4 border-b border-slate-700">
                  <h4 className="text-sm font-semibold text-emerald-400 flex items-center gap-2">
                    <Video className="w-4 h-4" /> One-Click Broadcast — No Software Needed
                  </h4>
                </div>
                <div className="p-4">
                  {ingestActive ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-3 bg-slate-900/60 rounded-lg p-3">
                        <video ref={ingestVideoRef} muted autoPlay playsInline className="w-32 h-24 bg-black rounded object-cover" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-white flex items-center gap-2">
                            <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                            Broadcasting: {ingestStreamName}
                          </p>
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            <button onClick={stopIngestBroadcast} className="text-xs bg-red-700 hover:bg-red-600 text-white px-3 py-1.5 rounded">Stop</button>
                            {ingestSessionResult && <>
                              <button onClick={() => { navigator.clipboard.writeText(ingestSessionResult.full_url); toast.success("Link copied!"); }}
                                className="text-xs bg-cyan-600 hover:bg-cyan-500 text-white px-3 py-1.5 rounded">Copy Link</button>
                              <a href={ingestSessionResult.full_url} target="_blank" rel="noopener noreferrer"
                                className="text-xs bg-blue-600 hover:bg-blue-500 text-white px-3 py-1.5 rounded flex items-center gap-1">
                                <ExternalLink className="w-3 h-3" /> View</a>
                              <button onClick={() => { setShowShareDialog(true); setSearchUserQuery(''); setSearchUsers([]); }}
                                className="text-xs bg-violet-600 hover:bg-violet-500 text-white px-3 py-1.5 rounded flex items-center gap-1">
                                <Users className="w-3 h-3" /> Share</button>
                            </>}
                          </div>
                        </div>
                      </div>
                      {ingestSessionResult && (
                        <div className="bg-emerald-900/30 border border-emerald-700/50 rounded p-3">
                          <p className="text-xs text-emerald-400 font-semibold mb-1">✅ Shareable Link</p>
                          <div className="flex items-center gap-2 bg-slate-900/80 rounded px-3 py-2">
                            <input readOnly value={ingestSessionResult.full_url} className="flex-1 bg-transparent text-white text-xs font-mono outline-none" />
                            <button onClick={() => { navigator.clipboard.writeText(ingestSessionResult.full_url); toast.success("Copied!"); }}
                              className="text-[10px] bg-cyan-600 hover:bg-cyan-500 text-white px-2 py-1 rounded shrink-0">Copy</button>
                          </div>
                          {/* FFmpeg command for hardware drones */}
                          {ingestSessionResult.push_command && (
                            <div className="mt-2 bg-slate-900/80 rounded p-2">
                              <p className="text-[10px] text-amber-400 font-semibold mb-1">🛸 DJI Drone Relay (2-step)</p>
                              <p className="text-[10px] text-slate-400 mb-1">Railway doesn't expose RTMP ports. Run this on a laptop connected to the same network as your drone:</p>
                              <code className="text-[10px] text-cyan-300 block break-all select-all font-mono leading-relaxed">
                                {ingestSessionResult.push_command}
                              </code>
                              <div className="flex gap-1.5 mt-2">
                                <button onClick={() => { navigator.clipboard.writeText(ingestSessionResult.push_command); toast.success("Command copied!"); }}
                                  className="text-[10px] bg-zinc-700 hover:bg-zinc-600 text-white px-2 py-1 rounded">Copy Command</button>
                                {ingestSessionResult.session_id && (
                                  <a href={`${API}/drone/session/${ingestSessionResult.session_id}/script`} download
                                    className="text-[10px] bg-zinc-700 hover:bg-zinc-600 text-white px-2 py-1 rounded flex items-center gap-1">
                                    <Download className="w-3 h-3" /> Download .bat Script
                                  </a>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      {showShareDialog && ingestSessionResult && <ShareStreamDialog streamName={ingestStreamName} shareableUrl={ingestSessionResult.full_url} streamType="broadcast" title={ingestStreamName} onClose={() => setShowShareDialog(false)} />}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <p className="text-xs text-slate-400">Broadcast from your browser. Works on any OS — no install needed. Share the link for live viewing.</p>
                      <div className="flex gap-2">
                        <button onClick={() => { setIngestSource("screen"); setShowIngestPanel(true); }}
                          className={`flex-1 border rounded-lg p-4 text-center transition-colors ${ingestSource === "screen" ? "border-emerald-500 bg-emerald-900/20" : "border-slate-700 bg-slate-900/40 hover:border-slate-500"}`}>
                          <Monitor className="w-6 h-6 mx-auto mb-1 text-cyan-400" />
                          <p className="text-sm font-medium text-white">Share Screen</p>
                          <p className="text-[10px] text-slate-400 mt-0.5">Window or full screen</p>
                        </button>
                        <button onClick={() => { setIngestSource("camera"); setShowIngestPanel(true); detectIngestDevices(); }}
                          className={`flex-1 border rounded-lg p-4 text-center transition-colors ${ingestSource === "camera" ? "border-emerald-500 bg-emerald-900/20" : "border-slate-700 bg-slate-900/40 hover:border-slate-500"}`}>
                          <Camera className="w-6 h-6 mx-auto mb-1 text-emerald-400" />
                          <p className="text-sm font-medium text-white">Use Camera</p>
                          <p className="text-[10px] text-slate-400 mt-0.5">CamLink / USB / Webcam</p>
                        </button>
                      </div>
                      {showIngestPanel && (
                        <div className="bg-slate-900/60 border border-slate-700 rounded-lg p-3 space-y-3">
                          {ingestSource === "camera" && (
                            <div>
                              <Label className="text-xs text-slate-400 mb-1 block">Camera</Label>
                              <select value={ingestSelectedDevice} onChange={e => setIngestSelectedDevice(e.target.value)}
                                className="w-full bg-slate-700 border border-slate-600 text-white text-xs rounded px-2 py-1.5 outline-none">
                                <option value="">Auto-detect</option>
                                {ingestDevices.map((d, i) => (
                                  <option key={d.deviceId + i} value={d.deviceId}>{d.label || `Camera ${i + 1}`}</option>
                                ))}
                              </select>
                            </div>
                          )}
                          <div className="flex items-center gap-2">
                            <div className="flex-1">
                              <Label className="text-xs text-slate-400 mb-1 block">Stream Name</Label>
                              <input value={ingestStreamName} onChange={e => setIngestStreamName(e.target.value)}
                                placeholder="Auto-generated" className="w-full bg-slate-700 border border-slate-600 text-white text-xs rounded px-2 py-1.5 outline-none" />
                            </div>
                            <button onClick={startIngestBroadcast}
                              className="bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2 rounded text-sm font-medium flex items-center gap-1.5 mt-5">
                              <Wifi className="w-4 h-4" /> Start
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </Card>

              {/* Broadcast from Camera — for CamLink / USB capture devices */}
              <Card className="bg-slate-800/80 border border-emerald-600/30">
                <div className="p-4 border-b border-slate-700">
                  <h4 className="text-sm font-semibold text-emerald-400 flex items-center gap-2">
                    <Video className="w-4 h-4" /> Broadcast Camera — CamLink / USB Devices
                  </h4>
                </div>
                <div className="p-4 space-y-3">
                  {camBroadcastActive ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-3 bg-slate-900/60 rounded-lg p-3">
                        <video ref={camPreviewRef} muted autoPlay playsInline className="w-32 h-24 bg-black rounded object-cover" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-white flex items-center gap-2">
                            <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                            Broadcasting: {camBroadcastName}
                          </p>
                          <p className="text-xs text-slate-400 mt-1">{camViewerCount} viewer{camViewerCount !== 1 ? 's' : ''}</p>
                          <div className="flex gap-1.5 mt-2">
                            <button onClick={stopCamBroadcast}
                              className="text-[10px] bg-red-700 hover:bg-red-600 text-white px-2 py-1 rounded">Stop Broadcast</button>
                            <button onClick={() => { setNewStreamName(camBroadcastName); setShowCreateSession(true); }}
                              className="text-[10px] bg-cyan-600 hover:bg-cyan-500 text-white px-2 py-1 rounded">Create Link</button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <p className="text-xs text-slate-400">Select a camera device to broadcast. CamLink capture cards appear as USB cameras.</p>
                      {camDevices.length > 0 ? (
                        <div className="flex items-center gap-2">
                          <select value={selectedCamDevice} onChange={e => setSelectedCamDevice(e.target.value)}
                            className="flex-1 bg-slate-700 border border-slate-600 text-white text-xs rounded px-2 py-1.5 outline-none">
                            <option value="">Select camera device...</option>
                            {camDevices.map((d, i) => (
                              <option key={d.deviceId + i} value={d.deviceId}>{d.label || `Camera ${i + 1}`}</option>
                            ))}
                          </select>
                          <button onClick={startCamBroadcast} disabled={!selectedCamDevice}
                            className="text-xs bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-600 text-white px-3 py-1.5 rounded font-medium flex items-center gap-1">
                            <Video className="w-3 h-3" /> Broadcast
                          </button>
                          <button onClick={enumerateCameras}
                            className="text-xs bg-slate-600 hover:bg-slate-500 text-white px-2 py-1.5 rounded" title="Refresh camera list">
                            <RefreshCw className="w-3 h-3" />
                          </button>
                        </div>
                      ) : (
                        <div className="text-center py-4 bg-slate-900/40 border border-dashed border-slate-700 rounded">
                          <Camera className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                          <p className="text-sm text-slate-400 mb-1">No cameras detected</p>
                          <p className="text-xs text-slate-500 mb-3">Click "Detect" to search for cameras, or "Broadcast" to try any available camera.</p>
                          <div className="flex items-center justify-center gap-2">
                            <button onClick={enumerateCameras}
                              className="text-xs bg-cyan-600 hover:bg-cyan-500 text-white px-3 py-1.5 rounded font-medium inline-flex items-center gap-1">
                              <Camera className="w-3 h-3" /> Detect Cameras
                            </button>
                            <button onClick={startCamBroadcast}
                              className="text-xs bg-emerald-600 hover:bg-emerald-500 text-white px-3 py-1.5 rounded font-medium inline-flex items-center gap-1">
                              <Video className="w-3 h-3" /> Broadcast (Any)
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </Card>

              {/* HTTP Push — bypass RTMP for Railway */}
              <Card className="bg-slate-800/80 border border-amber-600/30">
                <div className="p-4 border-b border-slate-700">
                  <h4 className="text-sm font-semibold text-amber-400 flex items-center gap-2">
                    <Terminal className="w-4 h-4" /> HTTP Push — For any Camera/Drone
                  </h4>
                </div>
                <div className="p-4 space-y-3">
                  <p className="text-xs text-slate-400">Push video via HTTP from any device. No RTMP port needed — works on any network.</p>
                  <div className="bg-slate-900/60 rounded p-3">
                    <p className="text-[10px] text-slate-500 mb-1">Windows (CamLink):</p>
                    <code className="text-xs text-amber-300 break-all font-mono">ffmpeg -f dshow -i video="CamLink" -c:v libvpx -b:v 2000k -f webm -method POST https://api.allbaise.com/api/drone/ingest/{'{stream_name}'}</code>
                  </div>
                  <div className="bg-slate-900/60 rounded p-3">
                    <p className="text-[10px] text-slate-500 mb-1">macOS:</p>
                    <code className="text-xs text-amber-300 break-all font-mono">ffmpeg -f avfoundation -i "0" -c:v libvpx -b:v 2000k -f webm -method POST https://api.allbaise.com/api/drone/ingest/{'{stream_name}'}</code>
                  </div>
                  <div className="bg-slate-900/60 rounded p-3">
                    <p className="text-[10px] text-slate-500 mb-1">Then create a session with the same stream name</p>
                    <p className="text-xs text-slate-300">Use "Generate Shareable Link" below with stream name matching your ingest name</p>
                  </div>
                </div>
              </Card>

              {/* Auto-Detect Panel — shows ALL active streams (RTMP + broadcast) */}
              <Card className="bg-slate-800/80 border border-slate-700">
                <div className="p-4 border-b border-slate-700">
                  <h4 className="text-sm font-semibold text-cyan-400 flex items-center gap-2">
                    <Wifi className="w-4 h-4" /> Auto-Detect — Active Feeds
                  </h4>
                </div>
                <div className="p-4">
                  {droneStreams.length > 0 ? (
                    <div className="space-y-2">
                      {droneStreams.map((s, i) => (
                        <div key={s.name + i} className="flex items-center justify-between bg-slate-900/50 border border-slate-700/50 px-3 py-2 rounded">
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 bg-emerald-500 rounded-full shrink-0" />
                            <span className="text-sm font-medium text-white">{s.name}</span>
                            <span className={`text-[10px] px-1 py-0.5 rounded ${
                              s.type === "broadcast" ? "bg-cyan-700 text-cyan-200" : "bg-amber-700 text-amber-200"
                            }`}>{s.type || "rtmp"}</span>
                            <span className="text-[10px] text-slate-400">{s.duration ? Math.floor(s.duration / 60) + "m" : "just now"}</span>
                          </div>
                          <button onClick={() => { setShowCreateSession(true); setNewStreamName(s.name); }}
                            className="text-[10px] bg-cyan-600 hover:bg-cyan-500 text-white px-2 py-1 rounded font-medium">
                            Create Link
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : droneSessions.length === 0 ? (
                    <div className="text-center py-6">
                      <Wifi className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                      <p className="text-sm text-slate-400 mb-1">No active feeds detected</p>
                      <p className="text-[10px] text-slate-500">Use "Broadcast Camera" above or push RTMP</p>
                    </div>
                  ) : (
                    <div className="text-center py-4 text-sm text-slate-500">No feeds, but sessions exist below</div>
                  )}
                </div>
              </Card>

              {/* Create Session Form */}
              {showCreateSession && (
                <Card className="bg-slate-800/80 border border-emerald-600/40">
                  <div className="p-4 border-b border-slate-700 flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-emerald-400">New Drone Session</h4>
                    <button onClick={() => setShowCreateSession(false)} className="text-slate-400 hover:text-white">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="p-4 space-y-4">
                    {createdSession ? (
                      <div className="space-y-3">
                        <div className="bg-emerald-900/30 border border-emerald-700/50 rounded p-3">
                          <p className="text-xs text-emerald-400 font-semibold mb-2">✅ Session Created — Shareable Link Ready</p>
                          <div className="flex items-center gap-2 bg-slate-900/80 rounded px-3 py-2">
                            <input readOnly value={createdSession.full_url} className="flex-1 bg-transparent text-white text-xs font-mono outline-none" />
                            <button onClick={() => { navigator.clipboard.writeText(createdSession.full_url); toast.success("Link copied!"); }}
                              className="text-[10px] bg-cyan-600 hover:bg-cyan-500 text-white px-2 py-1 rounded shrink-0">Copy</button>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          {createdSession.rtmp_url && (
                            <div className="bg-slate-900/50 border border-slate-700 rounded p-3 md:col-span-2">
                              <p className="text-[10px] text-slate-400 mb-1">RTMP URL (for DJI Pilot 2)</p>
                              <code className="text-xs text-cyan-300 break-all select-all">{createdSession.rtmp_url}</code>
                            </div>
                          )}
                          <div className="bg-slate-900/50 border border-slate-700 rounded p-3">
                            <p className="text-[10px] text-slate-400 mb-1">Stream Key</p>
                            <code className="text-xs text-amber-300">{createdSession.stream_key}</code>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <a href={createdSession.full_url} target="_blank" rel="noopener noreferrer"
                            className="text-xs bg-blue-600 hover:bg-blue-500 text-white px-3 py-2 rounded flex items-center gap-1">
                            <ExternalLink className="w-3 h-3" /> Open Viewer
                          </a>
                          {createdSession.session_id && (
                            <a href={`${API}/drone/session/${createdSession.session_id}/script`} download
                              className="text-xs bg-slate-600 hover:bg-slate-500 text-white px-3 py-2 rounded flex items-center gap-1">
                              <Download className="w-3 h-3" /> Get .bat Script
                            </a>
                          )}
                        </div>
                        {/* FFmpeg command for hardware drones */}
                        {createdSession.push_command && (
                          <div className="bg-amber-900/20 border border-amber-700/30 rounded p-3">
                            <p className="text-[10px] text-amber-400 font-semibold mb-1">🛸 FFmpeg Command (for hardware drones)</p>
                            <p className="text-[10px] text-slate-400 mb-2">Replace <strong>&lt;drone-rtmp&gt;</strong> with your drone's RTMP source address, then run this in terminal:</p>
                            <code className="text-[10px] text-cyan-300 block break-all select-all font-mono bg-slate-900/80 p-2 rounded leading-relaxed">
                              {createdSession.push_command}
                            </code>
                            <div className="flex gap-1.5 mt-2">
                              <button onClick={() => { navigator.clipboard.writeText(createdSession.push_command); toast.success("Command copied!"); }}
                                className="text-[10px] bg-zinc-700 hover:bg-zinc-600 text-white px-2 py-1 rounded">Copy Command</button>
                              {createdSession.session_id && (
                                <a href={`${API}/drone/session/${createdSession.session_id}/script`} download
                                  className="text-[10px] bg-zinc-700 hover:bg-zinc-600 text-white px-2 py-1 rounded flex items-center gap-1">
                                  <Download className="w-3 h-3" /> Download .bat
                                </a>
                              )}
                            </div>
                          </div>
                        )}
                        <div className="bg-emerald-900/20 border border-emerald-700/30 rounded p-3">
                          <p className="text-[10px] text-emerald-400 font-semibold mb-1">📋 DJI Drone Setup (Direct — no laptop needed)</p>
                          <ol className="text-[10px] text-slate-300 space-y-1.5 ml-4 list-decimal">
                            <li>Open <strong>DJI Pilot 2</strong> app on the Smart Controller</li>
                            <li>Go to <strong>Live Streaming</strong> settings</li>
                            <li>Select <strong>RTMP</strong> as the platform</li>
                            <li>Enter the <strong>RTMP URL</strong> shown above: <code className="text-cyan-300 text-[10px]">{createdSession.rtmp_url}</code></li>
                            <li>Set <strong>Stream Key</strong> to: <code className="text-amber-300 text-[10px]">{createdSession.stream_key}</code> (or leave blank)</li>
                            <li>Tap <strong>Start</strong> — drone feed goes live immediately</li>
                          </ol>
                          <div className="mt-2 bg-black/20 rounded p-2 flex items-center gap-2">
                            <span className="text-[10px] text-slate-400">View live:</span>
                            <a href={createdSession.full_url} target="_blank" rel="noopener noreferrer"
                              className="text-[10px] text-cyan-400 hover:text-cyan-300 underline">{createdSession.full_url}</a>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs text-slate-400 mb-1 block">Drone Model</Label>
                            <Select value={droneModel} onValueChange={setDroneModel}>
                              <SelectTrigger className="bg-slate-700 border-slate-600 text-white text-sm">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-slate-800 border-slate-700 text-white">
                                <SelectItem value="M350">DJI Matrice 350 RTK</SelectItem>
                                <SelectItem value="Mavic3Pro">DJI Mavic 3 Pro</SelectItem>
                                <SelectItem value="Mavic2">DJI Mavic 2</SelectItem>
                                <SelectItem value="Mavic3">DJI Mavic 3</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs text-slate-400 mb-1 block">Stream Name</Label>
                            <Input value={newStreamName} onChange={e => setNewStreamName(e.target.value)}
                              placeholder="Auto-generated if empty" className="bg-slate-700 border-slate-600 text-white text-sm" />
                          </div>
                        </div>
                        <button onClick={handleCreateDroneSession} disabled={creatingDroneSession}
                          className="w-full bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-600 text-white px-4 py-2 rounded text-sm font-medium flex items-center justify-center gap-2">
                          {creatingDroneSession ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wifi className="w-4 h-4" />}
                          {creatingDroneSession ? "Creating..." : "Generate Shareable Link"}
                        </button>
                      </>
                    )}
                  </div>
                </Card>
              )}

              {/* Active Sessions */}
              <Card className="bg-slate-800/80 border border-slate-700">
                <div className="p-4 border-b border-slate-700">
                  <h4 className="text-sm font-semibold text-white flex items-center gap-2">
                    <Video className="w-4 h-4 text-red-400" /> Active Drone Sessions
                  </h4>
                </div>
                <div className="p-4">
                  {droneSessions.length > 0 ? (
                    <div className="space-y-2">
                      {droneSessions.map(s => (
                        <div key={s.id} className="flex items-center justify-between bg-slate-900/50 border border-slate-700/50 px-3 py-2 rounded">
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <span className={`w-2 h-2 rounded-full shrink-0 ${s.is_live === false ? "bg-red-500" : "bg-emerald-500"}`} />
                            <span className="text-sm font-medium text-white truncate">{s.name}</span>
                            <span className="text-[10px] bg-slate-700 text-slate-300 px-1.5 py-0.5 rounded">{s.drone_model}</span>
                            <span className={`text-[10px] ${s.is_live === false ? "text-red-400" : "text-emerald-400"}`}>
                              {s.is_live === false ? "Offline" : "Live"}
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0 ml-2">
                            <button onClick={() => {
                              navigator.clipboard.writeText(`https://allbaise.com/drone/view/${s.share_token}`);
                              toast.success("Link copied!");
                            }} className="text-[10px] bg-cyan-600 hover:bg-cyan-500 text-white px-2 py-1 rounded">Copy Link</button>
                            <a href={`https://allbaise.com/drone/view/${s.share_token}`} target="_blank" rel="noopener noreferrer"
                              className="text-[10px] bg-blue-600 hover:bg-blue-500 text-white px-2 py-1 rounded flex items-center gap-1">
                              <ExternalLink className="w-3 h-3" /> View
                            </a>
                            <button onClick={() => handleDeleteDroneSession(s.id)}
                              className="text-[10px] bg-red-700 hover:bg-red-600 text-white px-2 py-1 rounded">End</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <Video className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                      <p className="text-sm text-slate-400">No active drone sessions</p>
                      <p className="text-[10px] text-slate-500 mt-1">Create a session above to get a shareable link</p>
                    </div>
                  )}
                </div>
              </Card>
            </TabsContent>

            {/* Smart Drone Tab — redirect to dedicated page */}
            <TabsContent value="smart-drone" className="space-y-4">
              <Card className="bg-app-card border-sky-600/30 p-6">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-sky-500 to-blue-700 flex items-center justify-center shrink-0">
                    <Camera className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-app mb-1">Smart Drone</h3>
                    <p className="text-sm text-app-secondary mb-4">
                      Full DJI drone session manager with multi-session support, live viewer,
                      snapshot/recording capture, and push-to-talk. Open the dedicated page for the full experience.
                    </p>
                    <div className="flex gap-2">
                      <Button onClick={() => navigate("/smart-drone")}
                        className="bg-sky-600 hover:bg-sky-500 text-white">
                        <Camera className="w-4 h-4 mr-2" /> Open Smart Drone
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            </TabsContent>

            {/* Drone Feeds Tab */}
            <TabsContent value="drones" className="space-y-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <Wifi className="w-4 h-4 text-cyan-400" /> Live Drone Feeds
                </h3>
                <div className="flex items-center gap-2">
                  {/* Go Live Button */}
                  <button onClick={() => { setBroadcastName("drone-" + Date.now().toString(36)); setShowBroadcastPanel(p => !p); setBroadcastError(null); }}
                    className="text-xs bg-red-600 hover:bg-red-500 text-white px-2 py-1 rounded flex items-center gap-1">
                    <Wifi className="w-3 h-3" /> {broadcastActive ? "Broadcasting" : "Go Live"}
                  </button>
                  <button onClick={fetchDroneStreams}
                    className="text-xs bg-slate-700 hover:bg-slate-600 text-white px-2 py-1 rounded flex items-center gap-1">
                    <RefreshCw className="w-3 h-3" /> Refresh
                  </button>
                </div>
              </div>

              {/* Broadcast Panel */}
              {(showBroadcastPanel || broadcastActive) && (
                <div className="bg-slate-800/80 border border-red-600/40 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-white flex items-center gap-2">
                      <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                      {broadcastActive ? "You are live" : "Go Live — Broadcast your screen"}
                    </h4>
                    {!broadcastActive && (
                      <button onClick={() => setShowBroadcastPanel(false)} className="text-slate-400 hover:text-white">
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  {!broadcastActive ? (
                    <>
                      {/* Setup form */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div className="md:col-span-2">
                          <Label className="text-xs text-slate-400 mb-1 block">Stream Name</Label>
                          <Input value={broadcastName} onChange={e => setBroadcastName(e.target.value)}
                            placeholder="e.g. drone-mavic-1" className="bg-slate-700 border-slate-600 text-white text-sm" />
                        </div>
                        <div className="flex items-end">
                          <button onClick={startBroadcast}
                            className="w-full bg-red-600 hover:bg-red-500 text-white px-4 py-2 rounded text-sm font-medium flex items-center justify-center gap-2">
                            <Wifi className="w-4 h-4" /> Start Broadcast
                          </button>
                        </div>
                      </div>
                      {broadcastError && (
                        <p className="text-red-400 text-xs flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" /> {broadcastError}
                        </p>
                      )}
                    </>
                  ) : (
                    <>
                      {/* Active broadcast status */}
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        {/* Preview */}
                        <div className="md:col-span-2 bg-black rounded-lg overflow-hidden aspect-video">
                          <video ref={broadcastPreviewRef} muted autoPlay playsInline className="w-full h-full object-cover" />
                        </div>
                        {/* Stats */}
                        <div className="md:col-span-2 space-y-2">
                          <div className="bg-slate-900/60 rounded-lg p-3">
                            <p className="text-xs text-slate-400">Stream Name</p>
                            <p className="text-white text-sm font-mono">{broadcastName}</p>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div className="bg-slate-900/60 rounded-lg p-3">
                              <p className="text-xs text-slate-400">Duration</p>
                              <p className="text-white text-sm font-mono">
                                {String(Math.floor(broadcastDuration / 60)).padStart(2, '0')}:
                                {String(broadcastDuration % 60).padStart(2, '0')}
                              </p>
                            </div>
                            <div className="bg-slate-900/60 rounded-lg p-3">
                              <p className="text-xs text-slate-400">Viewers</p>
                              <p className="text-white text-sm font-mono">{broadcastViewerCount}</p>
                            </div>
                          </div>
                          <button onClick={stopBroadcast}
                            className="w-full bg-red-700 hover:bg-red-600 text-white px-4 py-2 rounded text-sm font-medium flex items-center justify-center gap-2">
                            <Square className="w-4 h-4" /> Stop Broadcast
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {droneLoading ? (
                <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
              ) : droneStreams.length === 0 && !broadcastActive ? (
                <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-10 text-center">
                  <Wifi className="w-10 h-10 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400 text-sm mb-1">No active drone streams</p>
                  <p className="text-slate-500 text-xs">Click "Go Live" above to broadcast your screen</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {droneStreams.map((s, i) => (
                    <div key={s.name + i} className="bg-slate-800/60 border border-slate-700 rounded-lg overflow-hidden">
                      <div className="relative aspect-video bg-black">
                        {s.type === "broadcast" ? (
                          <BroadcastViewer streamName={s.name} relayApiUrl={RELAY_API} />
                        ) : (
                          <ReactPlayer url={s.hlsUrl} controls width="100%" height="100%"
                            playing muted playsinline className="react-player"
                            config={{ file: { forceHLS: true, hlsOptions: { liveSyncDuration: 2 } } }} />
                        )}
                        <div className="absolute top-2 left-2 flex items-center gap-1.5 bg-black/60 px-2 py-0.5 rounded">
                          <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                          <span className="text-[10px] text-green-400 font-medium">LIVE</span>
                        </div>
                        <div className="absolute top-2 right-2 bg-black/60 px-2 py-0.5 rounded text-[10px] text-slate-300 flex items-center gap-1">
                          {s.type === "broadcast" && (
                            <button onClick={() => setFullscreenStream({name: s.name, relayApiUrl: RELAY_API})}
                              className="hover:text-cyan-400 transition-colors" title="Fullscreen (prolonged monitoring)">
                              <Maximize2 className="w-3 h-3" />
                            </button>
                          )}
                          {s.duration ? Math.floor(s.duration / 60) + "m" : s.type === "broadcast" ? "LIVE" : "0m"}
                        </div>
                      </div>
                      <div className="p-2.5">
                        <p className="text-xs font-medium text-white truncate flex items-center gap-1">
                          {s.name}
                          {s.type === "broadcast" && (
                            <span className="text-[10px] text-cyan-400 bg-cyan-400/10 px-1 rounded">WebSocket</span>
                          )}
                          {s.viewerCount !== undefined && s.type === "broadcast" && (
                            <span className="text-[10px] text-slate-400 ml-auto">{s.viewerCount} viewer{(s.viewerCount||0)!==1?'s':''}</span>
                          )}
                        </p>
                        <div className="flex items-center justify-between mt-1">
                          {s.hlsUrl ? (
                            <>
                              <span className="text-[10px] text-slate-500 truncate">{s.hlsUrl}</span>
                              <button onClick={() => { navigator.clipboard.writeText(s.hlsUrl); toast.success("HLS URL copied"); }}
                                className="text-[10px] text-cyan-400 hover:text-cyan-300 shrink-0 ml-1">Copy</button>
                            </>
                          ) : (
                            <span className="text-[10px] text-cyan-400/60">WebSocket broadcast — share the Monitoring Centre link</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Alerts Tab */}
            <TabsContent value="alerts" className="space-y-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Active Alerts */}
                <div className="bg-slate-800 border border-slate-700">
                  <div className="p-4 border-b border-slate-700">
                    <h3 className="font-semibold text-white flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-red-500" />
                      Active Alerts
                    </h3>
                  </div>
                  <ScrollArea className="h-[400px]">
                    <div className="p-4 space-y-3">
                      {alerts.filter(a => a.status === "active").length > 0 ? (
                        alerts.filter(a => a.status === "active").map((alert) => (
                          <div key={alert.id} className={`border p-3 ${
                            alert.severity === "critical" ? "border-red-500 bg-red-500/10" :
                            alert.severity === "high" ? "border-amber-500 bg-amber-500/10" :
                            "border-slate-600 bg-slate-700/30"
                          }`}>
                            <div className="flex items-start justify-between">
                              <div>
                                <p className="text-white font-semibold">{alert.title}</p>
                                <p className="text-sm text-slate-400">{alert.description}</p>
                                <div className="flex items-center gap-2 mt-2">
                                  <span className={`badge text-xs ${
                                    alert.severity === "critical" ? "bg-red-500" :
                                    alert.severity === "high" ? "bg-amber-500" : "bg-blue-500"
                                  }`}>{alert.severity}</span>
                                  <span className="text-xs text-slate-500">{alert.type}</span>
                                </div>
                              </div>
                              <div className="flex gap-1">
                                <Button size="sm" variant="ghost" onClick={() => handleAcknowledgeAlert(alert.id)}>
                                  <CheckCircle className="w-4 h-4 text-emerald-400" />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => handleResolveAlert(alert.id)}>
                                  <XCircle className="w-4 h-4 text-slate-400" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-slate-400 text-center py-8">No active alerts</p>
                      )}
                    </div>
                  </ScrollArea>
                </div>

                {/* Motion Events */}
                <div className="bg-slate-800 border border-slate-700">
                  <div className="p-4 border-b border-slate-700">
                    <h3 className="font-semibold text-white flex items-center gap-2">
                      <Activity className="w-5 h-5 text-blue-500" />
                      Recent Motion Events
                    </h3>
                  </div>
                  <ScrollArea className="h-[400px]">
                    <div className="p-4 space-y-2">
                      {motionEvents.slice(0, 15).map((event) => (
                        <div key={event.id} className="flex items-center justify-between bg-slate-700/30 p-2 text-sm">
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${
                              event.intensity === "high" ? "bg-red-500" :
                              event.intensity === "medium" ? "bg-amber-500" : "bg-emerald-500"
                            }`} />
                            <span className="text-white">{event.camera_id}</span>
                          </div>
                          <span className="text-slate-400">{event.duration_seconds}s</span>
                          <span className="text-slate-500 text-xs">{event.zone}</span>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Monitor Bot Sidebar */}
        <div className="w-full lg:w-80 border-t lg:border-l border-slate-700 bg-slate-800/50 flex flex-col overflow-hidden">
          <HermesChat
            contextHint="[Monitoring Centre]"
            title="MONITOR BOT"
            icon={Bot}
            color="from-red-600 to-red-700"
            accentColor="text-red-400"
            layout="sidebar"
            placeholder="Ask Monitor..."
            sessionPrefix="monitoring"
          />
        </div>
      </div>
      {fullscreenStream && (
        <FullscreenBroadcastViewer
          streamName={fullscreenStream.name}
          relayApiUrl={fullscreenStream.relayApiUrl}
          onClose={() => setFullscreenStream(null)}
        />
      )}
    </div>
  );
}
